/**
 * @smile/shared — the contract between the API and the web client.
 *
 * Everything in here is derived from the *legacy* database, not from a
 * greenfield model. Where the legacy schema is odd (e.g. `user.status`
 * holding the role rather than an active flag) the oddity is documented
 * and preserved, because production still writes it that way.
 *
 * Split: ./rules.ts holds the enum tuples, catalogues and pure business
 * rules and imports nothing, so a zero-dependency harness can execute the
 * pricing logic. This file adds the Zod schemas on top and re-exports
 * everything, so consumers still import from '@smile/shared' only.
 */
import { z } from 'zod';
import { USER_ROLES, RESERVATION_STATUSES, RESERVATION_TYPES, PAYMENT_METHODS, CLUB_TIERS, PARKING_LOCATIONS, TRANSFER_DIRECTIONS, RESERVATION_SORTS, CLIENT_SORTS, CLIENT_STATUSES, CLIENT_STATUS_FILTERS, RESERVATION_STATUS_FILTERS, CLUB_FILTERS, SOURCE_FILTERS, RESERVATION_PER_PAGE, MAX_PAGE, } from './rules.js';
import { PARKING_SITES, SITE_FILTER_NONE } from './sites.js';
export * from './rules.js';
/* The five operational sites — Europort, Tuchoměřice, POP, Kněževes, Šestka —
   and the fold from the seventy raw `parking` strings onto them. Its own
   module because, like rules.ts, it imports nothing and is executed directly
   by a harness. */
export * from './sites.js';
/* How many places each site and each zone holds. Not in the legacy database
   for three of the five sites, so it is a file the owner edits until phase 1
   gives him a screen. Imports only a type, so it stays harness-executable. */
export * from './capacity.js';
/* Doporučené místo by rule — big car outside, Garáž to M (the owner, 30 Oct 2026). No runtime imports. */
export * from './autotarget.js';
export * from './referrer.js';
/* ------------------------------------------------------------------ *
 * Zod schemas over the enumerations above
 * ------------------------------------------------------------------ */
// Each pair below is a value (the Zod schema) plus a type of the same name.
// TypeScript merges them, and an explicit local export shadows the matching
// name coming from `export * from './rules.js'` — so there is exactly one
// UserRole in the public surface, and it is derived from USER_ROLES.
export const UserRole = z.enum(USER_ROLES);
export const ReservationStatus = z.enum(RESERVATION_STATUSES);
export const ReservationType = z.enum(RESERVATION_TYPES);
export const PaymentMethod = z.enum(PAYMENT_METHODS);
export const ClubTier = z.enum(CLUB_TIERS);
export const ClientSort = z.enum(CLIENT_SORTS);
export const ClientStatus = z.enum(CLIENT_STATUSES);
export const ParkingLocation = z.enum(PARKING_LOCATIONS);
export const TransferDirection = z.enum(TRANSFER_DIRECTIONS);
/**
 * One of the five operational sites.
 *
 * Not the same thing as ParkingLocation above, and the two must not be
 * conflated: ParkingLocation is the legacy `parkinglocation` column, which has
 * only ever held the pair the *booking form* offers. ParkingSite is where the
 * car is actually standing, derived in sites.ts from the `parking` column,
 * and it distinguishes the four yards the booking form lumps together.
 */
export const ParkingSite = z.enum(PARKING_SITES);
/**
 * A site, how many cars it accounts for, and the same figure by marked spot.
 *
 * Used twice and declared once: the dashboard's Obsazenost panel folds "where
 * every car currently on site is standing" into it, and the Převozy tab strip
 * folds "how many cars this queue owes each yard" into the same shape. They
 * are the same question asked over two different row sets, so they get one
 * type — and the second use is why this sits up here with the enums rather
 * than down in the Dashboard section where it was born.
 *
 * The Zod object mirrors the `SiteOccupancy` interface in sites.ts and shadows
 * it in the public surface, the same way UserRole shadows the tuple-derived
 * type above. sites.ts cannot declare it, because sites.ts imports nothing so
 * that a harness can run it before `npm install`; this file cannot own the
 * fold, because the fold has to be executable by that same harness. So the
 * shape is written twice, and `verify:sites` asserts the two agree by reading
 * the field names out of this declaration and comparing them against the keys
 * a real fold actually produces. A field added here and forgotten in
 * foldBySite — or the reverse — fails that harness.
 *
 * There are no capacity denominators here on purpose. The legacy panel prints
 * "367 / 483", but the `capacity` table holds nothing at all for POP, Kněževes
 * or Šestka, and for Tuchoměřice it sums to figures the legacy screen does not
 * print — so the denominators come from somewhere nobody has identified yet.
 * A number over a made-up denominator is worse than a number on its own.
 */
export const SiteOccupancy = z.object({
    /** null is the honest bucket: a spot string nothing recognises. */
    site: ParkingSite.nullable(),
    label: z.string(),
    total: z.number().int(),
    zones: z.array(z.object({ zone: z.string(), count: z.number().int() })),
});
/* ------------------------------------------------------------------ *
 * Session / auth
 * ------------------------------------------------------------------ */
export const SessionUser = z.object({
    id: z.number().int(),
    email: z.string(),
    name: z.string(),
    role: UserRole,
    /** Casbin-resolved permissions, flattened for the client to hide UI. */
    permissions: z.array(z.string()),
    /** True when the stored hash is still unsalted MD5 (legacy). */
    legacyPassword: z.boolean(),
});
export const LoginRequest = z.object({
    email: z.string().min(3).max(190),
    password: z.string().min(1).max(200),
});
/* ------------------------------------------------------------------ *
 * Reservations (read model)
 * ------------------------------------------------------------------ */
/**
 * One row of the Rezervace grid. Deliberately flat: the legacy screen is a
 * dense table and the client should not have to join anything client-side.
 */
/**
 * Odjezdy rows only (the owner, 30 Oct 2026: "show it in Odjezdy on the main page if the flight is
 * delayed"): the customer's return flight as the airport board has it — the same calculation as
 * Přílety. Null when the booking has no usable flight number or the board has no such flight.
 */
export const DepartureFlight = z.object({
    number: z.string(),
    verdict: z.enum(['ontime', 'later', 'earlier', 'beyond', 'cancelled']),
    terminal: z.string(),
    /** The landing used (real, expected or planned), Prague wall time. */
    landing: z.string().nullable(),
    landingKind: z.enum(['real', 'expected', 'planned']).nullable(),
    /** landing + the walk to the car, Prague wall time. */
    atCar: z.string().nullable(),
    /** landing − the time the customer wrote, minutes (+ = later). */
    flightDiffMinutes: z.number().int().nullable(),
    /** atCar − the time the customer wrote, minutes. */
    diffMinutes: z.number().int().nullable(),
    stateText: z.string(),
});
export const ReservationRow = z.object({
    id: z.number().int(),
    status: ReservationStatus,
    type: ReservationType,
    /** 'YYYY-MM-DD HH:MM:SS' in Europe/Prague, exactly as stored. */
    arrival: z.string(),
    departure: z.string(),
    arrivalReal: z.string().nullable(),
    departureReal: z.string().nullable(),
    created: z.string().nullable(),
    /** Billable days, computed with the legacy rule (see billableDays). */
    days: z.number().int(),
    driverName: z.string(),
    driverEmail: z.string(),
    driverPhone: z.string(),
    /** Free-text vehicle description. Staff also type plates/e-mails here. */
    car: z.string(),
    spz: z.string(),
    flightNumber: z.string(),
    /** Price locked at booking time. Never recomputed from the pricelist. */
    price: z.number(),
    priceOriginal: z.number().nullable(),
    /** The list the booking was priced on — what a requote must use. */
    pricelistId: z.number().int(),
    paid: z.number(),
    /** The services ordered, at the prices on the booking. */
    servicesPrice: z.number(),
    /** The late-departure penalty (rules.ts latePenalty), 0 for most. */
    penalty: z.number(),
    /** price + servicesPrice + penalty — the legacy `price_total`. */
    total: z.number(),
    /** total − paid — the legacy `price_topay`, the desk's "Zaplatit". */
    outstanding: z.number(),
    payment: PaymentMethod.nullable(),
    /**
     * `paid`, by channel — the four legacy columns the Odjelo card prints:
     * money taken on site (`normal_paid`, cash or card per `payment`), GoPay,
     * BitPay, and invoiced to a travel agency (`invoice_paid`, "Fakturace CK").
     * They sum to `paid` by construction.
     */
    paidOnSite: z.number(),
    paidGopay: z.number(),
    paidBitpay: z.number(),
    paidInvoice: z.number(),
    parking: z.string(),
    parkingLocation: ParkingLocation.nullable(),
    parkingTarget: z.string(),
    note: z.string(),
    person: z.number().int().nullable(),
    babycarseat: z.number().int().nullable(),
    clubUserId: z.number().int().nullable(),
    /** 'silver' | 'gold' | 'platinum' | null — from club_user.level. */
    clubTier: ClubTier.nullable(),
    /**
     * `reservation.user_id` — the extranet account that created the booking.
     * For partner traffic this is the partner's own user row.
     */
    partnerId: z.number().int().nullable(),
    partnerName: z.string().nullable(),
    /**
     * `reservation.partnerid` — an EXTERNAL reference string supplied by the
     * partner (e.g. 'ZZ10491158'). Despite the name it is not a foreign key
     * and must never be joined on.
     */
    partnerRef: z.string(),
    invoiceCompany: z.string(),
    invoiceStreet: z.string(),
    invoiceCity: z.string(),
    invoicePsc: z.string(),
    invoiceIc: z.string(),
    invoiceDic: z.string(),
    transferE2t: z.string().nullable(),
    transferT2e: z.string().nullable(),
    /** The KeyCafe service code (dispatchers, drivers), when the system made the key and it is not ended. Dashboard rows only. */
    keycafeService: z.string().nullable().default(null),
    /** Příjezdy rows only: the Doporučené místo was set by the system (autotarget), not by a person. */
    parkingTargetAuto: z.boolean().default(false),
    /** Odjezdy rows only: the return flight from the airport board (see DepartureFlight). */
    departureFlight: DepartureFlight.nullable().default(null),
    /**
     * Odjezdy rows only (the dispatchers' call of 30 Oct 2026, owner's yes 31 Oct): the KeyCafe box the
     * key was put back into for the customer, and when (Prague wall time) — the last DROPOFF judged
     * key_returned. Null when the wall has no such opening.
     */
    departureKey: z.object({ bin: z.string(), at: z.string() }).nullable().default(null),
    /**
     * Europort and Odjezdy rows (the dispatchers' call of 31 Oct 2026): is the key in a KeyCafe box right
     * now? The booking's last opening on the wall decides — a DROPOFF puts it in (`inBox`, which box,
     * when), a PICKUP takes it out (`inBox` false, when, the box it left). Null when the wall has never
     * seen this booking's key.
     */
    keyBox: z.object({ inBox: z.boolean(), bin: z.string(), at: z.string() }).nullable().default(null),
    /** Who drove each leg and which GPS unit went with the keys — the booking page's Převozy card. */
    transferE2tDriver: z.string().nullable().default(null),
    transferT2eDriver: z.string().nullable().default(null),
    transferE2tGps: z.number().nullable().default(null),
    transferT2eGps: z.number().nullable().default(null),
    keycafeArrival: z.string(),
    keycafeDeparture: z.string(),
    /* ---- what the legacy grid prints that the row did not carry before ---- */
    /** `pricelist.title` — the Ceník column. */
    pricelistTitle: z.string(),
    /** `reservation.promo` — the code typed at booking. */
    promo: z.string(),
    /** `reservation.source`: smileparking / dovolenaparking / valetparking / …; the Partner column's badge. */
    source: z.string(),
    /** GoPay: the gateway's last state ('PAID', 'CANCELED', …) and whether a session was ever opened. */
    gopayState: z.string(),
    gopaySession: z.string(),
    /** The legacy flags the Klient cell draws as icons. */
    needadmin: z.boolean(),
    carkey: z.boolean(),
    /** "Telefonicky ověřen" — the legacy `called` flag, set from the dashboard. */
    called: z.boolean(),
    reslock: z.boolean(),
    /** A club member who has set a password (`club_user.password` non-empty). */
    clubActive: z.boolean(),
    language: z.string(),
    /** `lastchange` within the last 31 minutes — the legacy's `recentupdate` row highlight. */
    recentUpdate: z.boolean(),
    /** The extra services on the booking, by catalogue id (icons in the Klient cell). */
    serviceIds: z.array(z.number().int()),
    /**
     * The public site's pay-online page for this booking — the legacy edit
     * form's "GoPay · Online platba" link, computed from the booking number
     * the way the old code did (the site, not this system, talks to GoPay).
     */
    payOnlineUrl: z.string().default(''),
});
export const ReservationSort = z.enum(RESERVATION_SORTS);
/**
 * A repeatable query parameter.
 *
 * Fastify's default querystring parser follows Node's `querystring.parse`:
 * `?status=closed` gives the string `'closed'`, and only `?status=closed&
 * status=paid` gives an array. A bare `z.array()` therefore rejects exactly
 * the case the toolbar produces most often — the single-select status box
 * sends one value — and the screen answers a perfectly ordinary filter with
 * `400 invalid_query` and an error card the operator cannot get out of.
 *
 * This is the one place that asymmetry is absorbed, so every multi-value
 * filter on every screen gets it. Widening it here rather than in each route
 * is deliberate: the defect was found on Statistiky and was already sitting,
 * unnoticed, in Rezervace.
 */
const many = (item) => z.preprocess((v) => (v == null || Array.isArray(v) ? v : [v]), z.array(item));
/**
 * Where the car is standing — the five yards plus the unattributed bucket.
 *
 * Built from PARKING_SITES rather than typed out, so a sixth yard becomes a
 * legal filter value in the same edit that teaches `siteOfSpot` about it.
 * `z.enum` needs a non-empty tuple, hence the head/tail spread.
 */
const SITE_FILTER_VALUES = [...PARKING_SITES, SITE_FILTER_NONE];
export const SiteFilter = z.enum(SITE_FILTER_VALUES);
/**
 * A boolean that survives a query string. Zod's coerce is `Boolean(value)`,
 * and `Boolean('false')` is true — so `?dynamic=false` arrived as true and
 * the sandbox's "bez obsazenosti" never worked over the API. This reads the
 * usual spellings of "no" as false and everything else the way coerce did.
 */
const FALSY = new Set(['false', '0', '', 'no', 'off']);
export const zBool = z.preprocess((v) => (typeof v === 'string' ? !FALSY.has(v.trim().toLowerCase()) : v), z.boolean());
export const ReservationStatusFilter = z.enum(RESERVATION_STATUS_FILTERS);
export const ClubFilter = z.enum(CLUB_FILTERS);
export const SourceFilter = z.enum(SOURCE_FILTERS);
/**
 * The Rezervace grid's query — the legacy filter box of page.reservations.php
 * (`$FILTERS['reservations']`: q, status, type, daterange, datetype,
 * needadmin, partner, extraservices, smileclub, source) plus paging and
 * sorting. Each field is the old control it stands for; the WHERE each one
 * produces is in buildReservationWhere, copied from the PHP.
 */
export const ReservationQuery = z.object({
    /**
     * Hledat: every word must occur somewhere in the legacy's fulltext column
     * set (id, name, e-mail, phone, note, promo, car, GoPay session, invoice
     * fields, pricelist title, flight, SPZ, source, partner reference).
     */
    q: z.string().trim().max(120).optional(),
    /** Stav: a group ('active', 'waiting', 'notdeleted') or single statuses. */
    status: many(ReservationStatusFilter).optional(),
    /** Parking: Venkovní / Garážové / VIP. */
    type: many(ReservationType).optional(),
    /** Datum: inclusive 'YYYY-MM-DD' bounds, read against `dateField`. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /**
     * The selector beside Datum: rezervace (created) / příjezd / odjezd. The
     * legacy default is `created`; příjezd and odjezd read the real stamp when
     * there is one (IFNULL(arrival_real, arrival)), as the old page did.
     */
    dateField: z.enum(['arrival', 'departure', 'created']).default('created'),
    /** "Vyžaduje pozornost administrátora!" */
    needadmin: zBool.optional(),
    /** Partner: the account that made the booking (`user_id`). */
    partnerId: z.coerce.number().int().optional(),
    /** Extra služby: bookings carrying ANY of these service ids. */
    services: many(z.coerce.number().int()).optional(),
    /** Smile Club. */
    club: ClubFilter.optional(),
    /** Web (zdroj). */
    source: SourceFilter.optional(),
    /* Ours, kept: the dashboard links here with them. */
    unpaidOnly: zBool.optional(),
    missingCarOnly: zBool.optional(),
    /**
     * One yard, or 'none' for the cars whose spot nobody recorded.
     *
     * Optional and with no default: absent means every site, which is what the
     * grid opens on. A default of 'europort' would be a filter the operator
     * never set and cannot see in the URL.
     */
    site: SiteFilter.optional(),
    /** The legacy default order: `-3`, column 3 of its SELECT — `created` DESC. */
    sort: ReservationSort.default('created'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
    perPage: z.coerce.number().int().min(10).max(500).default(RESERVATION_PER_PAGE),
});
export const ReservationPage = z.object({
    rows: z.array(ReservationRow),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
    /**
     * Aggregates over the WHOLE filtered set, not just the current page — the
     * legacy footer (`getGroupTotal`): "Celkem: N rezervací", the average
     * days, Σ price_total, Σ GoPay, Σ Fakturace CK, Σ Uhradit, and the two
     * "Placeno hotově / kartou" lines. `price`, `paid` and `outstanding` are
     * the older trio Statistiky still reads.
     */
    totals: z.object({
        price: z.number(),
        paid: z.number(),
        outstanding: z.number(),
        /** AVG(days) over the set — the legacy prints it under Dní. */
        avgDays: z.number(),
        /** Σ price_total (parking + services + penalty). */
        total: z.number(),
        gopay: z.number(),
        invoice: z.number(),
        /** Σ price_topay. */
        topay: z.number(),
        /** Σ over rows whose `payment` is cash / card of price_total − (GoPay + BitPay + invoice). */
        paidCash: z.number(),
        paidCard: z.number(),
    }),
    /**
     * True when the money sums in `totals` were zeroed because the caller may
     * not see company money (anyone without /reports/* read — every role but
     * admin; Codex F-01, 30 Oct 2026). `total` and `avgDays` stay real.
     */
    totalsHidden: z.boolean().optional(),
});
/* ------------------------------------------------------------------ *
 * Transfers / Převozy (read model)
 * ------------------------------------------------------------------ */
export const TransferRow = z.object({
    reservationId: z.number().int(),
    direction: TransferDirection,
    /**
     * The product tier. It is on this row because it decides where the car is
     * allowed to stand — Economy goes on the grass, Exclusive belongs in the
     * hall — so a dispatcher choosing a target spot needs it in front of them,
     * not one click away on the reservation.
     */
    type: ReservationType,
    driverName: z.string(),
    driverPhone: z.string(),
    car: z.string(),
    spz: z.string(),
    /** Shown next to the phone on the return queue: a delayed flight moves the job. */
    flightNumber: z.string(),
    arrival: z.string(),
    departure: z.string(),
    arrivalReal: z.string().nullable(),
    /** Where the car physically is right now (legacy `parking`). */
    parking: z.string(),
    parkingTarget: z.string(),
    /** Assigned transfer driver (user.id) and their display name. */
    assignedUserId: z.number().int().nullable(),
    assignedUserName: z.string().nullable(),
    /** Assigned GPS unit number (`transfer_*_gps`), null when unassigned. */
    gps: z.number().int().nullable(),
    /** Timestamp the transfer was dispatched, null while still queued. */
    dispatchedAt: z.string().nullable(),
    note: z.string(),
    clubTier: ClubTier.nullable(),
});
/* ------------------------------------------------------------------ *
 * Přílety (29 Oct 2026): when returning customers are really at the car
 * ------------------------------------------------------------------ */
export const ArrivalVerdict = z.enum(['ontime', 'later', 'earlier', 'beyond', 'cancelled', 'notfound', 'noflight']);
export const ArrivalFlight = z.object({
    /** The operating flight number as the airport shows it (EZY6613). */
    number: z.string(),
    airline: z.string(),
    origin: z.string(),
    originCode: z.string(),
    /** T1 (non-Schengen) or T2 (Schengen); '' when the airport gives none. */
    terminal: z.string(),
    /** Planned landing, Prague wall time. */
    scheduled: z.string(),
    /** The landing used: real once landed, else expected, else planned — Prague wall time. */
    landing: z.string().nullable(),
    landingKind: z.enum(['real', 'expected', 'planned']).nullable(),
    /** The airport's own words ("Arrived 21:29", "Estimate 23:10", "Cancelled"). */
    stateText: z.string(),
});
export const ArrivalRow = z.object({
    reservationId: z.number().int(),
    status: z.string(),
    type: ReservationType,
    driverName: z.string(),
    driverPhone: z.string(),
    car: z.string(),
    spz: z.string(),
    /** What the customer wrote in the flight field, unchanged. */
    flightRaw: z.string(),
    /** The time the customer wrote (booking `departure`), Prague wall time. */
    written: z.string(),
    /** The car has already gone back to the customer (status closed). */
    pickedUp: z.boolean(),
    flight: ArrivalFlight.nullable(),
    walkMinutes: z.number().int().nullable(),
    /** Landing + walk: when the customer is really at the car, Prague wall time. */
    atCar: z.string().nullable(),
    /** atCar − written, minutes: the pick-up delay (the 4-hour tolerance). */
    diffMinutes: z.number().int().nullable(),
    /** landing − written, minutes (+ = the flight lands later than the customer wrote). */
    flightDiffMinutes: z.number().int().nullable(),
    verdict: ArrivalVerdict,
});
export const ArrivalsResponse = z.object({
    rows: z.array(ArrivalRow),
    /** The window: from today 00:00 to the end of tomorrow, Prague wall time. */
    from: z.string(),
    to: z.string(),
    board: z.object({
        /** When the airport feed was last read, Prague wall time; null before the first read. */
        fetchedAt: z.string().nullable(),
        error: z.string().nullable(),
        flights: z.number().int(),
    }),
    rules: z.object({ t1Minutes: z.number().int(), t2Minutes: z.number().int(), toleranceMinutes: z.number().int(), ontimeMinutes: z.number().int() }),
});
export const TransferQuery = z.object({
    direction: TransferDirection,
    /** Only rows not yet dispatched. Default true — that is the work queue. */
    pendingOnly: zBool.default(true),
    limit: z.coerce.number().int().min(1).max(300).default(100),
    /**
     * One yard instead of all of them — the legacy strip's "Z POPu",
     * "Z Tuchoměřic", "Ze Šestky", "Z Kněževse".
     *
     * Omitted means the whole queue, which is what the screen opens on. It
     * filters `parking` — where the car is standing — which is why the screen
     * offers it on the return queue and not on the outbound one, where every
     * row is by definition standing at Europort. See TRANSFER_SITE_COL in
     * api/read/sql.ts for why there is no second column to filter on.
     *
     * `europort` parses here and is refused deeper down, by
     * `transferSitePrefixes`, with an error that says why: Europort is the hub
     * both directions run to and from, never an endpoint you filter one queue
     * by, and its spots are bare floor numbers that no LIKE prefix selects.
     * Excluding it from the enum at this layer would have made the error a flat
     * 400 "invalid enum value" instead of a sentence a maintainer can act on.
     */
    site: ParkingSite.optional(),
});
export const TransferQueues = z.object({
    e2t: z.array(TransferRow),
    t2e: z.array(TransferRow),
    /**
     * The tab badges.
     *
     * `e2t`/`t2e` are the queue totals. Both come from their own uncapped
     * `COUNT(*)`, NOT from measuring the row arrays: the arrays stop at the
     * hundred-row limit, and the legacy strip routinely shows "Z POPu 91" next
     * to "Z Tuchoměřic 68" — over the cap between them. A badge that quietly
     * reported the cap would tell a dispatcher the backlog had stopped growing
     * at the exact moment it started to matter.
     *
     * `returnBySite` splits the RETURN queue across the yards, and there is no
     * outbound counterpart because the schema holds no answer to that question:
     * a car waiting in the garage has not been assigned a yard yet — assigning
     * it is the dispatcher's next action. See TRANSFER_SITE_COL in
     * api/read/sql.ts for the evidence, and note that the legacy strip prints
     * exactly four yard tabs and every one of them says "Z", never "Do".
     *
     * It carries a Neurčeno entry when the queue holds a spot string the
     * classifier does not know, so the yard badges plus that one always add up
     * to `t2e`. That identity is asserted in verify:sql rather than left as a
     * claim here.
     */
    counts: z.object({
        e2t: z.number().int(),
        t2e: z.number().int(),
        returnBySite: z.array(SiteOccupancy),
    }),
});
/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */
/**
 * The shift the dashboard describes. The screen is not "today" — an operator
 * on nights at 02:00 is working a shift that began at 20:00 yesterday, and a
 * day-scoped dashboard would show them somebody else's work.
 */
export const ShiftWindow = z.object({
    kind: z.enum(['day', 'night']),
    /** 'YYYY-MM-DD HH:MM:SS', local time, inclusive — the nominal rota window. */
    from: z.string(),
    to: z.string(),
    /**
     * Where the shift's tallies really start: the last `balance.closed` — the
     * legacy `from_balance` (rules.ts legacyShiftAt). Odjelo, Propadlé and the
     * till count from here, not from `from`.
     */
    fromBalance: z.string(),
    /** Where the lists really end — the legacy `till_overlap`. */
    tillOverlap: z.string(),
});
/**
 * The till of a window — the legacy `Balance::getBalanceData` (read from its
 * source 18 October 2026): money by the moment it was TAKEN. `cash`/`card`
 * are desk payments (`payment` rows), `onsiteOther` the paystation's
 * auto_cash/auto_card, `gopay`/`invoice` prepaid money counted by the car's
 * real arrival. `bitpay` is kept for the shape and is always 0 (the legacy
 * till has no BitPay bucket). `cars` = bookings money came in for.
 */
export const TillBreakdown = z.object({
    cash: z.number(),
    card: z.number(),
    onsiteOther: z.number(),
    gopay: z.number(),
    bitpay: z.number(),
    invoice: z.number(),
    total: z.number(),
    /** Bookings money came in for during the window (the legacy `reservations_unique`). */
    cars: z.number().int(),
});
/**
 * One bar group on the shift chart: the whole hour, and the part of it that
 * still needs a car physically moved. The pending figures fall to zero for
 * hours already behind the clock, because that work is done.
 */
export const ChartBucket = z.object({
    /** 'HH' — the hour the group covers, e.g. '14'. */
    hour: z.string(),
    arrivals: z.number().int(),
    /** Of `arrivals`, the ones with the valet service — the legacy "Příjezdy Valet". */
    arrivalsValet: z.number().int(),
    departures: z.number().int(),
    departuresPending: z.number().int(),
});
/** A dashboard card: the rows shown plus the true total behind the badge. */
/**
 * One day of the occupancy outlook. `venku` and `garaz` are the cars expected
 * on site at the end of that day, by product; `arrivals` and `departures`
 * are that day's planned movements. Day 0 is today: the on-site count now,
 * before today's remaining movements.
 */
export const OutlookDay = z.object({
    /** 'YYYY-MM-DD' */
    date: z.string(),
    venku: z.number().int().min(0),
    garaz: z.number().int().min(0),
    total: z.number().int().min(0),
    arrivals: z.number().int().min(0),
    departures: z.number().int().min(0),
});
export const DashboardOutlook = z.object({
    /** Server clock when computed. */
    now: z.string(),
    days: z.array(OutlookDay),
});
/**
 * The handover protocol — the legacy "Prot." button: one sheet printed at
 * the desk when a car is taken in. The reservation, plus the catalogue of
 * extra services with a tick against each one that was ordered, so the
 * valet sees at a glance what is to be done to the car. Valet itself (id 2)
 * is left off the checklist, as the legacy leaves it off — it is the
 * driving, not a job done to a parked car.
 */
export const ProtocolService = z.object({
    id: z.number().int(),
    title: z.string(),
    price: z.number(),
    ordered: z.boolean(),
    /** 'YYYY-MM-DD HH:MM:SS' when the job is done, null while it waits or was never ordered. */
    done: z.string().nullable(),
});
export const ProtocolData = z.object({
    reservation: ReservationRow,
    services: z.array(ProtocolService),
});
/**
 * One line of the services checklist on an existing booking — the legacy
 * edit form's "Doplňkové služby": the live catalogue (valet included) plus
 * whatever the booking carries that the catalogue has since dropped.
 */
export const ReservationServiceLine = z.object({
    id: z.number().int(),
    title: z.string(),
    /** The booking's price when ordered, the catalogue's when not. */
    price: z.number(),
    listPrice: z.number(),
    ordered: z.boolean(),
    done: z.string().nullable(),
    /** False for a catalogue row since deleted — shown while ordered, never offered. */
    live: z.boolean(),
    /** Whether the catalogue offers it for this booking's product. */
    canOrder: z.boolean(),
});
export const ReservationServices = z.object({
    id: z.number().int(),
    services: z.array(ReservationServiceLine),
});
/* ------------------------------------------------------------------ *
 * Writes — Phase 1
 * ------------------------------------------------------------------ */
/**
 * "Přijel": mark a reservation's car as arrived, optionally with the spot it
 * was put on and the KeyCafe box the key went into. Both optional — the
 * legacy action was one click, and the spot is often recorded a minute
 * later by whoever parked the car.
 */
export const ArriveRequest = z.object({
    spot: z.string().trim().max(32).optional(),
    /** "Doporučené místo" — the yard place the car should go to (`parkingtarget`), set at the arrival as the old form
     *  did (the manager, 30 October 2026: club bookings come without one, and check-in sends them all to the 7th floor). */
    parkingTarget: z.string().trim().max(32).optional(),
    /** "Klíče od vozidla máme" — the legacy Přijel + klíče button. */
    carkey: z.boolean().optional(),
    /** Money taken at check-in — the legacy arrival form's "Zaplaceno". */
    payment: z
        .object({
        amount: z.number().int().positive().max(65_535),
        method: z.enum(['cash', 'card']),
    })
        .optional(),
    keycafeArrival: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,8}$/, 'KeyCafe kód: 1–8 znaků, písmena a číslice')
        .optional(),
});
/**
 * "Odjel": close the reservation, optionally taking money at the desk —
 * the legacy departure form's "Zaplaceno" (amount + hotově/kartou) or its
 * "Odjezd, předplaceno" when nothing is owed.
 */
/**
 * "Zaplaceno": money taken at the counter, without closing the booking —
 * the owner's fix of the old form, where paying also meant departing
 * (20 October 2026). The close is DepartRequest, a separate press.
 */
export const PaymentRequest = z.object({
    amount: z.number().int().positive().max(65_535),
    method: z.enum(['cash', 'card']),
});
export const DepartRequest = z.object({
    payment: z
        .object({
        // `normal_paid` is a SMALLINT UNSIGNED: 65 535 is the schema's ceiling.
        amount: z.number().int().positive().max(65_535),
        method: z.enum(['cash', 'card']),
    })
        .optional(),
    keycafeDeparture: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,8}$/, 'KeyCafe kód: 1–8 znaků, písmena a číslice')
        .optional(),
});
/**
 * "Přidělit převoz": hand a car to a driver for one leg. `target` is where
 * it is going — a yard spot for e2t, a Europort floor for t2e; the server
 * refuses the wrong side. `gps` is the unit handed over with the keys.
 */
export const DispatchRequest = z.object({
    direction: TransferDirection,
    driverId: z.number().int().positive(),
    target: z.string().trim().min(1).max(64),
    gps: z.number().int().min(1).max(99).nullable().optional(),
});
/**
 * "Opravit údaje": the fields the desk corrects on a booking — what the
 * legacy edit form's free-text inputs were. Every field optional; only the
 * ones sent are touched, and sending an empty string clears the field.
 * Limits are conservative for the legacy VARCHARs.
 */
export const EditRequest = z
    .object({
    driverName: z.string().trim().max(100),
    driverPhone: z.string().trim().max(32),
    driverEmail: z.string().trim().max(120),
    // Limits are the production VARCHAR widths (legacy-schema.sql): spz 12,
    // keycafe 8, flightnumber 32, parkingtarget 32. MySQL 5.5 in its default
    // mode truncates silently past them, which is worse than a 400.
    car: z.string().trim().max(255),
    spz: z.string().trim().max(12),
    flightNumber: z.string().trim().max(32),
    note: z.string().trim().max(2000),
    keycafeArrival: z.string().trim().max(8),
    keycafeDeparture: z.string().trim().max(8),
    /** Stání — the `parking` column; the location follows it on the server. */
    spot: z.string().trim().max(32),
    parkingTarget: z.string().trim().max(32),
    person: z.number().int().min(0).max(20),
    // The invoice block of the legacy form — the company a receipt is made
    // out to (widths from the schema: psc 8, ic/dic 63).
    invoiceCompany: z.string().trim().max(255),
    invoiceStreet: z.string().trim().max(255),
    invoiceCity: z.string().trim().max(255),
    invoicePsc: z.string().trim().max(8),
    invoiceIc: z.string().trim().max(63),
    invoiceDic: z.string().trim().max(63),
    /** The partner's own reference (`partnerid`, a string despite the name). */
    partnerRef: z.string().trim().max(16),
})
    .partial();
/**
 * "Doplňkové služby" on an existing booking — the legacy edit form's
 * checklist saved whole: every ticked service with the price beside it.
 * The API writes the difference against the booking (added, removed,
 * repriced), each journaled the legacy way.
 */
export const ServicesRequest = z.object({
    services: z
        .array(z.object({
        id: z.number().int().positive(),
        price: z.number().int().min(0).max(65_535).optional(),
    }))
        .max(50),
});
/* ------------------------------------------------------------------ *
 * Ceníky a cena — Phase 1b
 * ------------------------------------------------------------------ */
export const PricelistSummary = z.object({
    id: z.number().int(),
    title: z.string(),
    /** The promo codes that select it, as the legacy holds them: 'LETO10', 'GOOGLE10, FB10'. */
    promo: z.string(),
    dynamic: z.boolean(),
    capacityOutside: z.number().int(),
    capacityGarage: z.number().int(),
    /** Validity windows ('YYYY-MM-DD' pairs). Empty = always valid, as the legacy reads it. */
    periods: z.array(z.object({ from: z.string(), till: z.string() })),
});
/** The legacy default pricelist — `Pricelist::countPrice($pricelist_id = 58)`. */
export const DEFAULT_PRICELIST_ID = 58;
/* ------------------------------------------------------------------ *
 * Nastavení — ceníky (the legacy `pricelist` table, 96 day columns)
 * ------------------------------------------------------------------ */
export const PRICELIST_PRODUCTS = ['cheap', 'outside', 'inside'];
/** The three column families as the legacy names them → what the desk calls them. */
export const PRICELIST_PRODUCT_LABEL = {
    cheap: 'Venku',
    outside: 'Garáž',
    inside: 'Europort VIP',
};
export const PRICELIST_DAYS = 32;
const dayColumns = z.array(z.number().int().min(0).max(65_535)).length(PRICELIST_DAYS);
export const PricelistFull = z.object({
    id: z.number().int(),
    status: z.enum(['ok', 'deleted']),
    title: z.string(),
    note: z.string(),
    promo: z.string(),
    dynamic: z.boolean(),
    capacityOutside: z.number().int().nullable(),
    capacityGarage: z.number().int().nullable(),
    /** Index 0 = day 1 … index 30 = day 31, index 31 = each further day (the legacy day-32 rule). */
    columns: z.object({ cheap: dayColumns, outside: dayColumns, inside: dayColumns }),
    periods: z.array(z.object({ id: z.number().int(), from: z.string(), till: z.string() })),
    /** How many bookings sit on this list — the reason a list is retired, never deleted. */
    reservations: z.number().int(),
});
export const PricelistUpdate = z
    .object({
    status: z.enum(['ok', 'deleted']),
    title: z.string().trim().min(1).max(255),
    note: z.string().trim().max(2000),
    promo: z.string().trim().max(2000),
    dynamic: z.boolean(),
    capacityOutside: z.number().int().min(0).max(65_535).nullable(),
    capacityGarage: z.number().int().min(0).max(65_535).nullable(),
    columns: z.object({ cheap: dayColumns, outside: dayColumns, inside: dayColumns }).partial(),
})
    .partial();
export const PricelistCreate = z.object({
    title: z.string().trim().min(1).max(255),
    /** Copy the columns, capacities and flags of this list; blank otherwise. */
    copyFromId: z.number().int().positive().nullable().optional(),
});
export const PeriodCreate = z
    .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Od: YYYY-MM-DD'),
    till: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Do: YYYY-MM-DD'),
})
    .refine((v) => v.from <= v.till, { message: 'Konec období je před začátkem.' });
/* ------------------------------------------------------------------ *
 * Dynamické ceny v2 — seasons × filling × lead time, our own tables
 * ------------------------------------------------------------------ */
export const PRICING_ENGINES = ['legacy', 'v2'];
export const PricingEngine = z.enum(PRICING_ENGINES);
/** Which capacity a product fills: Venku the outside yard, Garáž the garage, Europort VIP its own places. */
export const PRICING_POOLS = ['outside', 'garage', 'exclusive'];
export const PricingPool = z.enum(PRICING_POOLS);
export const POOL_OF_TYPE = { cheap: 'outside', regular: 'garage', exclusive: 'exclusive' };
export const POOL_LABEL = { outside: 'Venku', garage: 'Garáž', exclusive: 'Europort VIP' };
const rateColumns = z.array(z.number().int().min(0).max(1_000_000)).length(PRICELIST_DAYS);
export const PricingSeason = z.object({
    id: z.number().int(),
    title: z.string(),
    /** 'YYYY-MM-DD' or null; both null = the base season, valid whenever nothing else is. */
    validFrom: z.string().nullable(),
    validTill: z.string().nullable(),
    /** Higher wins when two seasons cover the same arrival day. */
    priority: z.number().int(),
    status: z.enum(['ok', 'deleted']),
    note: z.string(),
    /** Per product, index 0 = day 1 … index 31 = each further day. Missing product = falls back to the base season. */
    rates: z.object({ cheap: rateColumns.nullable(), regular: rateColumns.nullable(), exclusive: rateColumns.nullable() }),
    updated: z.string().nullable(),
    updatedBy: z.string(),
});
export const PricingSeasonUpdate = z
    .object({
    title: z.string().trim().min(1).max(80),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    validTill: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    priority: z.number().int().min(-100).max(100),
    status: z.enum(['ok', 'deleted']),
    note: z.string().trim().max(2000),
    rates: z.object({ cheap: rateColumns.nullable(), regular: rateColumns.nullable(), exclusive: rateColumns.nullable() }).partial(),
})
    .partial();
export const PricingSeasonCreate = z.object({
    title: z.string().trim().min(1).max(80),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    validTill: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    priority: z.number().int().min(-100).max(100).optional(),
    /** Copy the rates of this season; blank otherwise. */
    copyFromId: z.number().int().positive().nullable().optional(),
});
/** One occupancy step: from this % of the pool's capacity, multiply by this. */
export const PricingBand = z.object({
    id: z.number().int(),
    pool: PricingPool,
    fromPct: z.number().int().min(0).max(100),
    factor: z.number().min(0.1).max(10),
});
/** One lead-time step: booked this many days or more before arrival, multiply by this. */
export const PricingLead = z.object({
    id: z.number().int(),
    fromDays: z.number().int().min(0).max(1000),
    factor: z.number().min(0.1).max(10),
});
export const PRICING_DISCOUNT_KINDS = ['percent', 'fixed'];
export const PricingDiscount = z.object({
    id: z.number().int(),
    /** The code the customer or the desk types; empty for a partner-bound discount. */
    code: z.string(),
    title: z.string(),
    kind: z.enum(PRICING_DISCOUNT_KINDS),
    /** Percent off (negative = surcharge), or crowns off. */
    value: z.number().int(),
    /** Set = applies to that partner account's bookings automatically. */
    userId: z.number().int().nullable(),
    /** Which products it applies to; empty = all. */
    products: z.array(ReservationType),
    validFrom: z.string().nullable(),
    validTill: z.string().nullable(),
    status: z.enum(['ok', 'deleted']),
    note: z.string(),
});
export const PricingDiscountUpdate = z
    .object({
    code: z.string().trim().max(40).regex(/^[A-Za-z0-9_-]*$/, 'Kód: písmena, číslice, - a _'),
    title: z.string().trim().max(120),
    kind: z.enum(PRICING_DISCOUNT_KINDS),
    value: z.number().int().min(-100_000).max(100_000),
    userId: z.number().int().positive().nullable(),
    products: z.array(ReservationType),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    validTill: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    status: z.enum(['ok', 'deleted']),
    note: z.string().trim().max(2000),
})
    .partial();
export const PricingBandsUpdate = z.object({
    pool: PricingPool,
    steps: z.array(z.object({ fromPct: z.number().int().min(0).max(100), factor: z.number().min(0.1).max(10) })).max(20),
});
export const PricingLeadsUpdate = z.object({
    steps: z.array(z.object({ fromDays: z.number().int().min(0).max(1000), factor: z.number().min(0.1).max(10) })).max(20),
});
export const PRICING_SETTING_KEYS = ['engine', 'round_to', 'max_factor', 'min_factor', 'capacity_outside', 'capacity_garage', 'capacity_exclusive'];
export const PricingSettings = z.object({
    engine: PricingEngine,
    /** Round the final price to a multiple of this (1 or 10). */
    roundTo: z.number().int(),
    maxFactor: z.number(),
    minFactor: z.number(),
    /** The pools' capacities the occupancy % is measured against. */
    capacityOutside: z.number().int(),
    capacityGarage: z.number().int(),
    capacityExclusive: z.number().int(),
    /**
     * Přetékání: on peak nights outdoor cars are parked in covered spaces (the
     * owner, 28 October 2026), so an outdoor or garage quote also looks at how
     * full the two pools are together, and takes the tighter of the two
     * percentages. Off: each pool against its own capacity only.
     */
    overflow: z.boolean(),
    /** True when nothing has been set up yet — the screen offers the import. */
    empty: z.boolean(),
});
export const PricingSettingsUpdate = z
    .object({
    engine: PricingEngine,
    roundTo: z.union([z.literal(1), z.literal(10)]),
    maxFactor: z.number().min(1).max(10),
    minFactor: z.number().min(0.1).max(1),
    capacityOutside: z.number().int().min(1).max(5000),
    capacityGarage: z.number().int().min(1).max(5000),
    capacityExclusive: z.number().int().min(1).max(5000),
    overflow: z.boolean(),
})
    .partial();
/** Everything the Dynamické ceny screen shows, in one payload. */
export const PricingConfig = z.object({
    settings: PricingSettings,
    seasons: z.array(PricingSeason),
    bands: z.array(PricingBand),
    leads: z.array(PricingLead),
    discounts: z.array(PricingDiscount),
});
/** The v2 breakdown that rides on a Quote when the v2 engine priced it. */
export const QuoteV2 = z.object({
    season: z.object({ id: z.number().int(), title: z.string() }),
    /** True when the product's rates came from the base season because the matched season had none. */
    fromBase: z.boolean(),
    base: z.number(),
    pool: PricingPool,
    capacity: z.number().int(),
    booked: z.number().int(),
    occupancyPct: z.number(),
    /** With přetékání on, for outdoor/garage: all non-VIP cars against both pools together at the same tightest hour; null otherwise. */
    totalPct: z.number().nullable().default(null),
    /** Which percentage chose the band: the pool's own or the total. */
    bandBy: z.enum(['pool', 'total']).default('pool'),
    band: z.object({ fromPct: z.number().int(), factor: z.number() }).nullable(),
    leadDays: z.number().int(),
    lead: z.object({ fromDays: z.number().int(), factor: z.number() }).nullable(),
    /** band × lead after the min/max caps. */
    factor: z.number(),
    /** base × factor, rounded — before any discount. */
    listPrice: z.number(),
    discount: z
        .object({ id: z.number().int(), code: z.string(), title: z.string(), kind: z.enum(PRICING_DISCOUNT_KINDS), value: z.number().int(), amount: z.number() })
        .nullable(),
    /** What the legacy engine (list 58, dynamic) would have said — the parallel-run comparison. */
    legacyPrice: z.number().nullable(),
});
export const Quote = z.object({
    days: z.number().int(),
    pricelistId: z.number().int(),
    pricelistTitle: z.string(),
    /** The promo that selected the pricelist, when one did. */
    promo: z.string().nullable(),
    /** False when the arrival falls outside every validity window of the list. */
    inPeriod: z.boolean(),
    /** The column price before the occupancy multiplier. */
    base: z.number(),
    /** 1, 1.2, 1.3, 1.4, 1.5, 1.6 or 2 — the legacy ladder. 1 when the list is not dynamic. */
    factor: z.number(),
    /** Free places at the tightest hour of the stay, as the ladder saw them. Null when not dynamic. */
    remaining: z.number().int().nullable(),
    price: z.number(),
    /** What the default list would charge, when a promo list came out cheaper. Null otherwise. */
    prevPrice: z.number().nullable(),
    /** The legacy park closures on the arrival and departure days — a warning, not a refusal. */
    closed: z.object({ arrival: z.boolean(), departure: z.boolean() }),
    /** Which engine priced it; 'legacy' unless Nastavení → Dynamické ceny switched v2 on. */
    engine: PricingEngine.default('legacy'),
    v2: QuoteV2.nullable().default(null),
});
export const QuoteQuery = z.object({
    /** 'YYYY-MM-DD HH:MM' or with seconds; Prague wall-clock. */
    arrival: z.string().min(16).max(19),
    departure: z.string().min(16).max(19),
    type: ReservationType.default('cheap'),
    pricelistId: z.coerce.number().int().positive().optional(),
    promo: z.string().trim().max(32).optional(),
    /** false = the base price without the occupancy multiplier (the legacy's `$dynamic_price`). */
    dynamic: zBool.default(true),
    /** v2: the partner account whose own discount applies. */
    partnerId: z.coerce.number().int().positive().optional(),
    /** Force one engine (the settings sandbox compares them); default = the switch. */
    engine: PricingEngine.optional(),
});
/**
 * "Nová rezervace" — the legacy reservations-edit form, the fields a desk
 * fills for a walk-in or a phone booking. The price is the quote unless the
 * desk types another (the legacy let admin/manager/operator set it freely);
 * services are catalogue ids and are priced from the catalogue.
 */
/** One live catalogue service, with which products may order it. */
export const ServiceCatalogItem = z.object({
    id: z.number().int(),
    title: z.string(),
    /** The catalogue's English and German names (empty when not filled in) — for the website and partners. */
    titleEn: z.string().default(''),
    titleDe: z.string().default(''),
    price: z.number(),
    canOrder: z.object({ cheap: z.boolean(), regular: z.boolean(), exclusive: z.boolean() }),
    included: z.object({ regular: z.boolean(), exclusive: z.boolean() }),
});
export const CreateRequest = z.object({
    type: ReservationType,
    /** 'YYYY-MM-DD HH:MM' Prague wall-clock. */
    arrival: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/, 'Příjezd: datum a čas'),
    departure: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/, 'Odjezd: datum a čas'),
    driverName: z.string().trim().min(1, 'Jméno je povinné.').max(255),
    driverPhone: z.string().trim().max(255).default(''),
    driverEmail: z.string().trim().max(255).default(''),
    car: z.string().trim().max(255).default(''),
    spz: z.string().trim().max(12).default(''),
    flightNumber: z.string().trim().max(32).default(''),
    note: z.string().trim().max(2000).default(''),
    person: z.number().int().min(0).max(20).default(0),
    pricelistId: z.number().int().positive().optional(),
    promo: z.string().trim().max(63).optional(),
    /** Omitted = the quote. `price` is a SMALLINT UNSIGNED in the schema. */
    price: z.number().int().min(0).max(65_535).optional(),
    services: z.array(z.number().int().positive()).max(20).default([]),
});
export const CreateReceipt = z.object({
    id: z.number().int(),
    price: z.number(),
    /** Parking plus the services — the legacy `price_original`. */
    total: z.number(),
    days: z.number().int(),
    pricelistId: z.number().int(),
    at: z.string(),
});
/* ------------------------------------------------------------------ *
 * Interní zprávy — the legacy `message` table
 * ------------------------------------------------------------------ */
export const InternalMessage = z.object({
    id: z.number().int(),
    title: z.string(),
    content: z.string(),
    /** 'YYYY-MM-DD HH:MM:SS' — shown from, and until. */
    releaseDate: z.string(),
    endDate: z.string(),
    updated: z.string(),
    userId: z.number().int(),
    userName: z.string(),
});
/**
 * A new message for the desk. Shown from now; `days` is how long, the
 * legacy default being two. No draft state — the legacy had one and 0 of
 * 298 rows ever used it.
 */
export const MessageCreate = z.object({
    title: z.string().trim().min(1, 'Nadpis je povinný.').max(120),
    content: z.string().trim().min(1, 'Text je povinný.').max(4000),
    days: z.number().int().min(1).max(60).default(2),
});
/**
 * "Uzavřít směnu": the legacy dashboard's close-shift form — a note and the
 * people who worked the shift (the `balance_user` roster). Whoever presses
 * the button is always on the roster.
 */
export const CloseShiftRequest = z.object({
    note: z.string().trim().max(2000).default(''),
    userIds: z.array(z.number().int().positive()).max(20).default([]),
});
export const CloseShiftReceipt = z.object({
    id: z.number().int(),
    /** Prague wall-clock, as `balance.started` holds it. */
    started: z.string(),
    /** Prague wall-clock — the moment it closed, as a person reads it. */
    closed: z.string(),
    userIds: z.array(z.number().int()),
});
/* ------------------------------------------------------------------ *
 * LPR — the gate events parking-lpr pushes (INTERFACE.md §4)
 * ------------------------------------------------------------------ */
export const LPR_SITES = ['knezeves', 'tuchomerice', 'pop', 'sestka'];
export const LprEvent = z.object({
    event_id: z.string().trim().min(8).max(64),
    site: z.enum(LPR_SITES),
    direction: z.enum(['entry', 'exit']),
    plate: z.string().trim().min(1).max(16),
    plate_raw: z.string().trim().max(32).default(''),
    country: z.string().trim().max(4).default(''),
    confidence: z.number().min(0).max(1),
    camera_id: z.string().trim().max(64).default(''),
    /** RFC 3339 with an explicit offset — §3. */
    observed_at: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2}|Z)$/, 'observed_at: RFC 3339 s posunem'),
    image_ref: z.string().trim().max(255).default(''),
});
/** What became of an event once it was stored. */
export const LPR_OUTCOMES = [
    'applied', // entry, one open reservation matched above threshold → Přijel
    'review', // entry matched below threshold, or ambiguous → a dispatcher decides
    'no_match', // entry, no open reservation with that plate
    'exit_matched', // exit, a car on site with that plate — the desk closes it (money)
    'exit_unmatched', // exit, nothing on site with that plate: misread, or an unrecorded leaver
];
export const LprOutcome = z.enum(LPR_OUTCOMES);
export const LprEventRow = z.object({
    eventId: z.string(),
    site: z.string(),
    direction: z.string(),
    plate: z.string(),
    plateRaw: z.string(),
    confidence: z.number(),
    cameraId: z.string(),
    observedAt: z.string(),
    imageRef: z.string(),
    /** 'YYYY-MM-DD HH:MM:SS' UTC, when the API stored it. */
    receivedAt: z.string(),
    reservationId: z.number().int().nullable(),
    outcome: LprOutcome,
    note: z.string(),
});
/* ------------------------------------------------------------------ *
 * KeyCafe — the key wall at Europort (docs/KEYCAFE.md)
 * ------------------------------------------------------------------ */
/**
 * One door opening as KeyCafe reports it — by webhook or by the poll. The
 * shape is KeyCafe's own event (docs.keycafe.com, "Retrieve Event"); only
 * what we read is required, the rest may come or not.
 */
export const KeycafeWebhookEvent = z
    .object({
    id: z.coerce.number().int().positive(),
    dateCreated: z.string().min(16),
    type: z.enum(['DROPOFF', 'PICKUP']),
    bin: z.string().max(16).nullable().optional(),
    accessMethod: z.string().max(32).nullable().optional(),
    anonymous: z.boolean().optional(),
    offline: z.boolean().optional(),
    location: z.object({ id: z.coerce.number().int().optional(), name: z.string().optional() }).passthrough().optional(),
    key: z
        .object({ id: z.coerce.number().int().optional(), name: z.string().optional(), serialNumber: z.string().optional() })
        .passthrough()
        .optional(),
    user: z.object({ id: z.coerce.number().int().optional() }).passthrough().optional(),
    access: z.object({ id: z.coerce.number().int().optional() }).passthrough().optional(),
})
    .passthrough();
/**
 * What the rules decided for one door opening (keycafe/events.ts). The
 * `would_*` outcomes are `listen` mode: stored and compared, nothing written.
 */
export const KEYCAFE_OUTCOMES = [
    'arrival',
    'key_taken',
    'key_returned',
    'departure',
    'would_arrival',
    'would_key_taken',
    'would_key_returned',
    'would_departure',
    'no_booking',
    'ignored',
    'refused',
    /** Write mode: stored, the stamp not yet confirmed — a crash here is resumed by the poller. */
    'pending_arrival',
    'pending_departure',
    /** A pick-up the wall alone cannot place (no return trip on record): left to the desk and the phone page. */
    'review',
    /** The customer's drop when the arrival is already recorded (phone check-in page, or the desk): nothing to stamp. */
    'arrival_known',
    'would_arrival_known',
];
export const KeycafeOutcome = z.enum(KEYCAFE_OUTCOMES);
export const KeycafeEventRow = z.object({
    eventId: z.number().int(),
    type: z.enum(['DROPOFF', 'PICKUP']),
    bin: z.string(),
    accessMethod: z.string(),
    keyId: z.number().int().nullable(),
    keyName: z.string(),
    locationId: z.number().int().nullable(),
    /** 'YYYY-MM-DD HH:MM:SS' Prague wall-clock, when the door opened. */
    occurredAt: z.string(),
    receivedAt: z.string(),
    source: z.enum(['webhook', 'poll']),
    reservationId: z.number().int().nullable(),
    outcome: KeycafeOutcome,
    note: z.string(),
});
/** A key the new system created for a booking (keys made by hand have no row). */
export const KeycafeKeyRow = z.object({
    reservationId: z.number().int(),
    keyId: z.number().int(),
    code: z.string(),
    name: z.string(),
    createdAt: z.string(),
    createdBy: z.number().int().nullable(),
});
/**
 * One of the three codes the system makes for a booking's key (the owner,
 * 26 October 2026): `dropoff` — the customer's, on the booking as
 * `keycafecode_arrival`; `pickup` — the customer's for collecting, on the
 * booking as `keycafecode_departure`; `service` — the dispatchers' and
 * drivers', shown only in the new system. `state`: `claim`/`made` while it is
 * being made, `dormant` (the service code before the customer's drop),
 * `live`, `ended` (cancelled in KeyCafe — the wall refuses it).
 */
export const KEYCAFE_ACCESS_KINDS = ['dropoff', 'pickup', 'service'];
export const KeycafeAccessRow = z.object({
    kind: z.enum(KEYCAFE_ACCESS_KINDS),
    accessId: z.number().int(),
    code: z.string(),
    state: z.enum(['claim', 'made', 'dormant', 'live', 'ended']),
    startsAt: z.string().nullable(),
    changedAt: z.string(),
    note: z.string(),
});
/** The booking page's KeyCafe card: the code on the booking, our key if any, the door openings. */
export const KeycafeBookingView = z.object({
    reservationId: z.number().int(),
    code: z.string(),
    key: KeycafeKeyRow.nullable(),
    /** The three codes, when the system made the key (empty for a key made by hand). */
    accesses: z.array(KeycafeAccessRow).default([]),
    /** KEYCAFE_DROPOFF_HOURS: how long the drop-off code lives after the drop. */
    dropoffHours: z.number().default(24),
    events: z.array(KeycafeEventRow),
    /** What the last judged door opening says about the key: in a bin, taken out, collected, or unknown. */
    keyState: z.enum(['unknown', 'in_box', 'with_driver', 'collected', 'taken']),
    bin: z.string().nullable(),
});
/**
 * The plate as both sides compare it — §3: uppercase, no spaces, hyphens or
 * dots. Applied to the event's plate AND to the reservation's `spz` before
 * matching, because the desk types plates every way there is.
 */
export function normalizePlate(raw) {
    return raw.toUpperCase().replace(/[\s.\-]/g, '');
}
/**
 * "Nepřijel" / "Zrušit": the two ways a booking ends without a car — the
 * legacy `q_noshow` and the Zrušeno status on its edit form. A note goes
 * to the journal, not the row, so the booking's own note stays the
 * customer's.
 */
/**
 * Two uses of one route. Without `correction`: Nepřijel / Zrušit from the
 * board — a booking that never became a car, strict. With `correction`:
 * the booking page's Stav row, the legacy edit form's eight statuses set
 * by hand (undo a wrong close, delete a duplicate, mark an agency booking
 * Předplaceno) — the owner, 20 October 2026: "we use all of it".
 */
export const StatusRequest = z.object({
    to: z.enum(RESERVATION_STATUSES),
    note: z.string().trim().max(255).default(''),
    correction: z.boolean().default(false),
});
/**
 * Money that did not pass through the desk — the legacy edit form's three
 * admin-only fields: GoPay, BitPay, and "Fakturace CK" (invoiced to a
 * travel agency). Absolute amounts, as the form held them; only the ones
 * sent are touched.
 */
export const PrepaidRequest = z
    .object({
    gopay: z.number().int().min(0).max(65_535),
    bitpay: z.number().int().min(0).max(65_535),
    invoice: z.number().int().min(0).max(65_535),
})
    .partial();
/**
 * The receipt (the legacy `printreceive`): what the desk hands over with
 * the keys at departure. Numbered from the `receive` table — one number
 * per reservation, issued on first print, reused on every reprint.
 */
export const ReceiptLine = z.object({ title: z.string(), amount: z.number() });
export const ReceiptData = z.object({
    reservation: ReservationRow,
    /** 'PI' + the sequence, as the legacy printed it. */
    number: z.string(),
    /** Prague wall-clock, when the number was issued. */
    issuedAt: z.string(),
    issuedBy: z.string(),
    /** Date of taxable supply — departure_real, else the issue time. */
    taxDate: z.string(),
    lines: z.array(ReceiptLine),
    total: z.number(),
    prepaid: z.array(ReceiptLine),
    toPay: z.number(),
    /** 21 %: base = round(amount / 1.21, 2), vat = amount − base — the legacy's own arithmetic. */
    vat: z.object({ rate: z.number(), base: z.number(), vat: z.number(), on: z.number() }),
    paymentLabel: z.string(),
});
/** Dobropis — the credit note (STORNO) for a receipt. */
export const CreditNoteKind = z.enum(['full', 'services', 'price_penalty']);
export const CreditNoteData = z.object({
    reservation: ReservationRow,
    number: z.string(),
    kind: CreditNoteKind,
    issuedAt: z.string(),
    issuedBy: z.string(),
    taxDate: z.string(),
    lines: z.array(ReceiptLine),
    total: z.number(),
    paymentLabel: z.string(),
});
/* ------------------------------------------------------------------ *
 * Nastavení — kapacity
 * ------------------------------------------------------------------ */
export const CapacitySetting = z.object({
    key: z.string(),
    label: z.string(),
    /** The number the file ships with (shared/src/capacity.ts). */
    shipped: z.number().int(),
    /** The owner's number, null while none was ever saved. */
    value: z.number().int().nullable(),
    updated: z.string().nullable(),
    updatedBy: z.string(),
});
export const CapacityUpdate = z.object({
    values: z.record(z.string(), z.number().int().min(0).max(5000)),
});
/* ------------------------------------------------------------------ *
 * Nastavení — uzavírky (the legacy `closure` table)
 * ------------------------------------------------------------------ */
/** One closed day: which of the four gates are shut, who shut them, when. */
export const Closure = z.object({
    /** 'YYYY-MM-DD' */
    day: z.string(),
    arrival: z.boolean(),
    departure: z.boolean(),
    arrivalEuroport: z.boolean(),
    departureEuroport: z.boolean(),
    note: z.string(),
    setBy: z.string(),
    /** 'YYYY-MM-DD HH:MM:SS' Prague */
    setAt: z.string(),
});
export const CLOSURE_FIELDS = ['arrival', 'departure', 'arrivalEuroport', 'departureEuroport'];
export const ClosureField = z.enum(CLOSURE_FIELDS);
export const ClosureUpdate = z.object({
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Den: YYYY-MM-DD'),
    field: ClosureField,
    closed: z.boolean(),
    note: z.string().trim().max(255).optional(),
});
/* ------------------------------------------------------------------ *
 * Nastavení — služby (the legacy `extraservice` catalogue)
 * ------------------------------------------------------------------ */
export const ServiceCatalogFull = z.object({
    id: z.number().int(),
    status: z.enum(['ok', 'deleted']),
    title: z.string(),
    titleEn: z.string(),
    titleRu: z.string(),
    titleDe: z.string(),
    price: z.number().int(),
    priority: z.number().int(),
    public: z.boolean(),
    canOrder: z.object({ cheap: z.boolean(), regular: z.boolean(), exclusive: z.boolean() }),
    included: z.object({ regular: z.boolean(), exclusive: z.boolean() }),
});
/** Every field the legacy extraservices-edit form had; all optional on an update. */
export const ServiceUpdate = z
    .object({
    status: z.enum(['ok', 'deleted']),
    title: z.string().trim().min(1).max(255),
    titleEn: z.string().trim().max(255),
    titleRu: z.string().trim().max(255),
    titleDe: z.string().trim().max(255),
    price: z.number().int().min(0).max(16_777_215),
    priority: z.number().int().min(0).max(255),
    public: z.boolean(),
    canOrderCheap: z.boolean(),
    canOrderRegular: z.boolean(),
    canOrderExclusive: z.boolean(),
    includedRegular: z.boolean(),
    includedExclusive: z.boolean(),
})
    .partial();
/** What every successful write answers with. */
export const WriteReceipt = z.object({
    id: z.number().int(),
    /** The columns that changed, in journal order. Empty means nothing did. */
    changed: z.array(z.string()),
    /** 'YYYY-MM-DD HH:MM:SS' on the row and in the journal. */
    at: z.string(),
});
/** The answer to the services checklist save: what the difference came to. */
export const ServicesReceipt = WriteReceipt.extend({
    added: z.array(z.number().int()),
    removed: z.array(z.number().int()),
    repriced: z.array(z.number().int()),
});
/** The answer to POST /api/dashboard/auto-verify: which bookings arrived by themselves. */
export const AutoVerifyReceipt = z.object({
    ids: z.array(z.number().int()),
    at: z.string(),
});
/**
 * "Změnit termín": the legacy edit form's date, product and price fields
 * on an existing booking — a flight moved, a longer stay, a different
 * product. Only the fields sent change; the price follows the new term on
 * the booking's own pricelist unless the desk sends one, as the legacy let
 * admin/manager/operator do.
 */
export const RescheduleRequest = z
    .object({
    arrival: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/, 'Příjezd: datum a čas'),
    departure: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/, 'Odjezd: datum a čas'),
    type: ReservationType,
    price: z.number().int().min(0).max(65_535),
    /** false = keep the current price whatever the term does. Default true: requote. */
    requote: z.boolean(),
    note: z.string().trim().max(255),
})
    .partial();
export const RescheduleReceipt = WriteReceipt.extend({
    days: z.number().int(),
    price: z.number(),
    /** What the pricelist says for the new term — shown beside the price that was kept. */
    quoted: z.number().nullable(),
});
/**
 * One line of a reservation's change history — the legacy
 * `reservation_journal`, one row per changed column. `item` is the column
 * name as the legacy names it (`arrival_real`, `parking`, `status`, …);
 * the values are the raw strings the journal holds, which for the datetime
 * columns means unix seconds — the legacy convention, kept so both systems
 * read one history. The screen turns those back into a time.
 */
export const JournalEntry = z.object({
    /** 'YYYY-MM-DD HH:MM:SS' in UTC — a TIMESTAMP column; the screen converts. */
    changed: z.string(),
    item: z.string(),
    newValue: z.string(),
    oldValue: z.string(),
    userId: z.number().int(),
    /** Empty when the user row is gone or the write was the system's. */
    userName: z.string(),
    note: z.string(),
    /** When the value is a user id (a transfer's driver, the partner): that person's name, else null. */
    newUserName: z.string().nullable().default(null),
    oldUserName: z.string().nullable().default(null),
});
export const ReservationJournal = z.object({
    id: z.number().int(),
    entries: z.array(JournalEntry),
});
/** Journal items whose values are unix seconds, not text. */
export const JOURNAL_EPOCH_ITEMS = new Set([
    'arrival',
    'departure',
    'arrival_real',
    'departure_real',
    'transfer_e2t',
    'transfer_t2e',
    'created',
    'lastchange',
]);
/** The shape a refused write answers with, alongside its HTTP status. */
export const WriteRefusal = z.object({
    error: z.enum(['not_found', 'conflict', 'invalid', 'locked', 'writes_disabled']),
    message: z.string(),
    detail: z.record(z.unknown()).optional(),
});
export const DashboardSection = z.object({
    rows: z.array(ReservationRow),
    /** Total matching rows, which may exceed rows.length when capped. */
    total: z.number().int(),
});
/**
 * One tab of the K převozu card: a yard, its badge, and its cars.
 *
 * The legacy dashboard prints this as a strip along the top of the screen —
 * "Europort 40, Z Tuchoměřic 68, Z POPu 91, Ze Šestky 0, Z Kněževse 38" — and
 * the dispatchers have read it that way for years. It is one card with tabs
 * here rather than five cards because five cards is five headings, five
 * badges and up to three hundred rows on a screen that already carries seven
 * lists, and because the five are not five subjects: they are one queue —
 * cars that must be at Europort before their customer is — sliced by where
 * the car happens to be standing this minute.
 *
 * `site` is null for the honest bucket, and it is a bucket that really occurs:
 * ten cars in the busiest window of the staging extract have a blank `parking`
 * column. They are cars somebody has to go and find, so they get a tab.
 */
export const DashboardTransferTab = z.object({
    site: ParkingSite.nullable(),
    /** 'Z Tuchoměřic', 'Ze Šestky', … — genitive, as the legacy strip spells it. */
    label: z.string(),
    /** Cars in this yard's slice of the queue, uncapped. */
    total: z.number().int(),
    rows: z.array(ReservationRow),
});
/**
 * The K převozu card: the whole queue, plus the same queue split by yard.
 *
 * `rows`/`total` are the "Vše" tab and mean exactly what they mean on every
 * other card. `tabs` always carries all five sites in reading order, including
 * the ones at zero — a yard that drops off the strip when it empties is a yard
 * a dispatcher stops checking — plus a trailing Neurčeno only when something
 * failed to classify.
 *
 * Every `tabs[].total` comes off an uncapped `GROUP BY`, so the badges sum to
 * `total` however deep the queue gets. The `rows` arrays are capped like every
 * other card's, and verify:sql pins the sum identity against the real data.
 */
export const DashboardTransferSection = DashboardSection.extend({
    tabs: z.array(DashboardTransferTab),
});
/**
 * One outstanding job on a car that is on site — a wash, an interior clean,
 * an ozone treatment.
 *
 * It extends the reservation row rather than replacing it because the card is
 * read the same way as every other card on the dashboard: an operator wants
 * the plate, the customer and the spot before they want the job. The three
 * added fields are what makes it a job rather than a booking.
 *
 * One row per job, not per car. A car booked for a wash and an interior clean
 * appears twice, which is correct: that is two pieces of work.
 */
export const ServiceRow = ReservationRow.extend({
    /**
     * The catalogue title, or `Služba #123` when the catalogue row is gone.
     *
     * Seven rows in the archive point at ids that no longer exist. A job that
     * vanishes off a worklist because its label was deleted is worse than a job
     * labelled with its number, so the label is never empty.
     */
    service: z.string(),
    /** `reservation_extraservice.extraservice_id`, raw — the identity of the job. */
    serviceRef: z.number().int(),
    /** Price charged for this service on this booking, not the catalogue price. */
    servicePrice: z.number(),
    /**
     * Which stretch of the worklist the job is in — the owner's order of
     * 4 October 2026, read top to bottom: cars leaving this shift or the next
     * (the deadline is close), cars already brought back to Europort and
     * waiting for their customer with the job still open, the rest of the cars
     * on the yards, and finally bookings that have not arrived yet but carry a
     * service, so the team sees what is coming. The API sorts by this, then by
     * departure (arrival for the last group).
     */
    group: z.enum(['soon', 'europort', 'later', 'arrivals']),
});
export const SERVICE_GROUPS = ['soon', 'europort', 'later', 'arrivals'];
/** The headings the Služby card draws over each stretch. */
export const SERVICE_GROUP_LABEL = {
    soon: 'Odjíždějí brzy — tato a příští směna',
    europort: 'Na Europortu — čekají na zákazníka',
    later: 'Ostatní na parkovišti',
    arrivals: 'Příjezdy — ještě nepřijely',
};
/** The Služby card: outstanding jobs shown, plus the true total behind them. */
export const DashboardServices = z.object({
    rows: z.array(ServiceRow),
    total: z.number().int(),
});
/**
 * Which cars are standing in one zone of one car park.
 *
 * The occupancy panel answers "how many are at Tráva E"; this answers "which
 * ones", which is the question a dispatcher actually has when they walk out
 * there. It is a request of its own rather than a field on the dashboard
 * payload because the answer is fourteen hundred rows across forty zones and
 * the operator wants one of them — fetching all of it every sixty seconds to
 * satisfy a click that may never come is the wrong trade in an office on a
 * shared line.
 *
 * The zone is a raw `parking` string, not a label. `NO_ZONE` is the single
 * exception and means "the column is blank", because that is a real bucket on
 * the panel and it has to be openable like any other.
 */
export const ZoneCarsQuery = z.object({
    /* Non-empty: an omitted zone would otherwise select every car on every
       site, which is the one answer this endpoint must never give. Bounded
       because it goes into a LIKE-free equality against an indexed column and
       there is no reason for it to be longer than a spot name. */
    zone: z.string().min(1).max(80),
    limit: z.coerce.number().int().min(1).max(300).default(200),
});
export const ZoneCars = z.object({
    /** Echoed back so a late answer cannot be painted under a new heading. */
    zone: z.string(),
    rows: z.array(ReservationRow),
    /** Cars really standing there, uncapped — `rows` may be shorter. */
    total: z.number().int(),
});
/**
 * What the dashboard accepts: one optional yard.
 *
 * The owner's note was "the filters from and where … in the dashboard do not
 * exist". This is that filter, and it is one field rather than a copy of
 * `ReservationQuery` on purpose — the dashboard is not a grid. Its window is
 * the shift, decided by the server clock, and every other axis a dispatcher
 * might want (dates, status, paid) is a question the Rezervace grid already
 * answers better. Adding them here would produce a second, subtly different
 * search on the one screen that is supposed to be glanceable.
 *
 * `site` reuses `SiteFilter`, so the dashboard and the grid cannot come to
 * disagree about what 'pop' or 'none' selects.
 */
export const DashboardQuery = z.object({
    site: SiteFilter.optional(),
    /**
     * Which shift the board describes: 0 (default) is the current one, 1 the
     * next, up to three days out. The occupancy panel, the services queue and
     * the on-site count are always "now" regardless — a forecast of where cars
     * will be standing is not something the data can give.
     */
    shift: z.coerce.number().int().min(0).max(6).optional(),
});
export const DashboardStats = z.object({
    /** 'YYYY-MM-DD' the figures describe (the date the shift started). */
    date: z.string(),
    /** Server clock at the moment the figures were computed. */
    now: z.string(),
    shift: ShiftWindow,
    /**
     * 0 when `shift` is the current one; otherwise how many shifts ahead it is.
     * The screen reads this, not the clock, to decide whether it is showing a
     * forecast — the API is the one that chose the window, so it says so.
     */
    shiftOffset: z.number().int().min(0),
    /**
     * The yard filter this payload was computed under, echoed back, or null.
     *
     * Echoed for the same reason `ZoneCars.zone` is: the screen refreshes every
     * sixty seconds and a dispatcher can change the chip while a request is in
     * flight, so a late answer must be identifiable as belonging to the old
     * one rather than painted under the new heading.
     *
     * It scopes the six section lists and their badges, and NOTHING else. The
     * day counters, the till, the chart and the occupancy panel stay whole,
     * deliberately: they are the figures staff reconcile against, occupancy IS
     * the per-site breakdown, and a header count that silently changed meaning
     * with a chip would be a number two people could read differently while
     * both looking at the screen.
     */
    site: SiteFilter.nullable(),
    arrivalsToday: z.number().int(),
    departuresToday: z.number().int(),
    onSiteNow: z.number().int(),
    pendingE2t: z.number().int(),
    pendingT2e: z.number().int(),
    unpaidCount: z.number().int(),
    unpaidAmount: z.number(),
    missingCarCount: z.number().int(),
    /**
     * How many bookings the legacy board would have marked arrived on this
     * load (page.dashboard-beta.php's silent `status = verified` over complete
     * self-drive bookings in the window; write/sql.ts AUTO_VERIFY_PREDICATE).
     * The read does not do it; when this is above zero the browser asks
     * POST /api/dashboard/auto-verify to, then refetches. Always 0 on a
     * forecast shift.
     */
    autoVerifyDue: z.number().int(),
    till: TillBreakdown,
    /** The till was cut out: this user may not close the shift (a driver, 30 Oct 2026). */
    tillHidden: z.boolean().optional(),
    /** Only what the desk took in on site (cash, card, paystation): GoPay and invoices left out (30 Oct 2026). */
    tillOnsiteOnly: z.boolean().optional(),
    /** One entry per hour of the shift, in order. */
    chart: z.array(ChartBucket),
    /**
     * Where those `onSiteNow` cars actually are, by site and by spot.
     *
     * Always all five sites, in reading order, even when a site is empty — plus
     * a trailing "Neurčeno" entry only when something failed to classify. The
     * totals sum to exactly `onSiteNow` because both come from the same
     * predicate; verify:sql asserts it against the staging database rather than
     * leaving it as a comment.
     */
    occupancy: z.array(SiteOccupancy),
    /**
     * The owner's capacity numbers (Nastavení → Kapacity), keyed as
     * shared/capacity.ts CapacityOverrides. Empty until he saves one; the
     * screens fall back to the shipped file.
     */
    capacities: z.record(z.string(), z.number().int()),
    sections: z.object({
        arrivals: DashboardSection,
        departures: DashboardSection,
        parked: DashboardSection,
        left: DashboardSection,
        lapsed: DashboardSection,
        /* The one card with a tab strip; see DashboardTransferSection. */
        toTransfer: DashboardTransferSection,
        /**
         * The legacy "Europort" tab: cars physically at the airport, not yet
         * driven to a yard. The other direction of `toTransfer`, and not
         * shift-scoped — see the section spec.
         */
        europort: DashboardSection,
    }),
    /**
     * The work outstanding in the yard: services ordered on cars that are here
     * and not yet ticked off. The legacy dashboard prints this as its own tab.
     */
    services: DashboardServices,
    /** 24 buckets, index = hour of day. Kept for the day-level view. */
    hourlyArrivals: z.array(z.number().int()).length(24),
    hourlyDepartures: z.array(z.number().int()).length(24),
});
/* ------------------------------------------------------------------ *
 * Catalogues
 * ------------------------------------------------------------------ */
export const StaffMember = z.object({
    id: z.number().int(),
    name: z.string(),
    role: UserRole,
});
/* ------------------------------------------------------------------ *
 * Uživatelé — the legacy `users` list and `settings` account form
 * ------------------------------------------------------------------ */
export const USER_LIST_PER_PAGE = 50;
/** The legacy filter: one status, or 'ok' for everyone who can sign in. */
export const UserQuery = z.object({
    q: z.string().trim().max(120).optional(),
    status: z.enum([...USER_ROLES, 'ok']).optional(),
    page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
});
export const UserListRow = z.object({
    id: z.number().int(),
    name: z.string(),
    email: z.string(),
    role: UserRole,
    pricelistId: z.number().int(),
    pricelistTitle: z.string(),
    invoicePayment: z.boolean(),
    provision: z.number().int(),
    promos: z.array(z.string()),
    reservationCount: z.number().int(),
    /** Prague wall-clock of the last `userlogin` row, or null. */
    lastLogin: z.string().nullable(),
});
export const UserList = z.object({
    rows: z.array(UserListRow),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
});
/** The account form — everything the legacy `settings` page showed. */
export const UserDetail = UserListRow.extend({
    note: z.string(),
    partnerCompany: z.string(),
    partnerStreet: z.string(),
    partnerCity: z.string(),
    partnerPsc: z.string(),
    partnerIc: z.string(),
    partnerDic: z.string(),
    /** Whether the stored password is still a legacy (unsalted) hash. */
    legacyPassword: z.boolean(),
});
const userText = (max) => z.string().trim().max(max);
/** What an administrator may change on any account. All optional. */
export const UserUpdate = z
    .object({
    name: userText(63).min(1),
    email: z.string().trim().toLowerCase().email().max(63),
    role: UserRole,
    pricelistId: z.number().int().min(1).max(65_535),
    invoicePayment: z.boolean(),
    provision: z.number().int().min(0).max(100),
    /** Comma-separated codes, as the legacy stored them. */
    promo: userText(255),
    note: userText(2000),
    partnerCompany: userText(255),
    partnerStreet: userText(255),
    partnerCity: userText(255),
    partnerPsc: userText(8),
    partnerIc: userText(63),
    partnerDic: userText(63),
})
    .partial();
/** A new account: the legacy generated its password and showed it once. */
export const UserCreate = UserUpdate.required({ name: true, email: true, role: true });
export const UserCreated = z.object({
    id: z.number().int(),
    /** Shown once; never stored in the clear. */
    password: z.string(),
});
/** What a person may change on their own account. */
export const AccountUpdate = UserUpdate.pick({
    name: true,
    email: true,
    partnerCompany: true,
    partnerStreet: true,
    partnerCity: true,
    partnerPsc: true,
    partnerIc: true,
    partnerDic: true,
});
export const PASSWORD_MIN = 8;
export const PasswordChange = z.object({
    current: z.string().min(1).max(200),
    next: z.string().min(PASSWORD_MIN, `Heslo musí mít aspoň ${PASSWORD_MIN} znaků.`).max(200),
});
/* ------------------------------------------------------------------ *
 * Uzávěrky / Směny (shift closes)
 * ------------------------------------------------------------------ */
/**
 * One closed shift, with the money that passed through it.
 *
 * Two fields carry the same instant on purpose. `closed` is the Prague
 * wall-clock time the shift actually ended; `closedStored` is the raw string
 * in the database, which the legacy application wrote in UTC. They differ by
 * an hour in winter and two in summer, and the difference is not cosmetic —
 * on July 2026 data, 13% of the month's takings fall inside the window the
 * stored value leaves out. Both are exposed so that a manager comparing this
 * screen against the old one can see immediately why the numbers differ,
 * instead of concluding that one of them is broken.
 */
export const BalanceRow = z.object({
    id: z.number().int(),
    /** 'YYYY-MM-DD HH:MM:SS', Prague local — stored correctly. */
    started: z.string(),
    /** 'YYYY-MM-DD HH:MM:SS', Prague local — corrected from the stored UTC. */
    closed: z.string(),
    /** The raw value in the `balance` table, which is UTC. */
    closedStored: z.string(),
    /** Whole minutes between `started` and the corrected `closed`. */
    minutes: z.number().int(),
    /**
     * Which rota slot the shift belongs to, from the hour it began. Shifts that
     * start well away from 08:00 or 20:00 are real — handovers slip, and a few
     * hundred rows in the history are split or doubled shifts — so there is a
     * third value rather than a forced guess.
     */
    kind: z.enum(['day', 'night', 'other']),
    /** The operator who pressed the button. Accountability, not staffing. */
    closedById: z.number().int().nullable(),
    closedByName: z.string(),
    /** Everyone recorded as working the shift, from `balance_user`. */
    staff: z.array(StaffMember),
    note: z.string(),
    /** Legacy flag: the shift's reservations were locked at close. */
    locked: z.boolean(),
    till: TillBreakdown,
});
export const BalanceQuery = z.object({
    /** Inclusive date bounds, 'YYYY-MM-DD', applied to the shift's start. */
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
    /**
     * Capped well below the reservation grid's 500. Each listed shift costs one
     * aggregate query for its till, so the page size is also a query budget.
     */
    perPage: z.coerce.number().int().min(5).max(50).default(20),
});
export const BalancePage = z.object({
    rows: z.array(BalanceRow),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
    /**
     * The sum of the tills of the shifts ON THIS PAGE — deliberately not of the
     * whole filtered range. Summing a range would mean assuming the shifts in it
     * are contiguous and non-overlapping, which the history does not guarantee,
     * and a total that is subtly wrong is worse than one that is narrow. The
     * screen labels this as the visible shifts, so the figure and its caption
     * cannot come apart.
     */
    totals: TillBreakdown,
});
/* ------------------------------------------------------------------ *
 * Klienti — Smile Parking Club members
 * ------------------------------------------------------------------ */
/**
 * One row of `club_profile`: an identity the member books under. A member can
 * have many (a company account books for a dozen drivers), and `club_user`
 * points at one of them via `prefered_id`.
 *
 * The dialling prefix is a separate column in the legacy schema and is joined
 * to the number for display in the browser, never in SQL — MySQL 5.5 and
 * sqlite disagree about string concatenation and this is not worth a dialect
 * argument.
 */
export const ClientProfile = z.object({
    id: z.number().int(),
    /** 'new' | 'ok' | 'deleted'. Legacy writes 'new' and rarely promotes it. */
    status: z.string(),
    /** The profile's own label, e.g. a company car's nickname. Often empty. */
    name: z.string(),
    driverName: z.string(),
    driverEmail: z.string(),
    driverPhone: z.string(),
    phonePrefix: z.string(),
    car: z.string(),
    spz: z.string(),
    invoiceCompany: z.string(),
    invoiceStreet: z.string(),
    invoiceCity: z.string(),
    invoicePsc: z.string(),
    invoiceIc: z.string(),
    invoiceDic: z.string(),
    /** True when this is the member's `prefered_id`. */
    preferred: z.boolean(),
});
/**
 * The loyalty ledger for one member, in the three groups the legacy screen
 * prints: earned, spent, left over.
 *
 * `earnedTotal` includes affiliate points, while `earnedParking` and
 * `earnedExtras` are the member's OWN points only. That is not an oversight,
 * it is the legacy column set reproduced exactly, and it means the identity
 * that holds is earnedTotal = earnedParking + earnedExtras + earnedAffiliate.
 *
 * `remaining*` subtract redemptions from ALL earned points of that kind (own
 * plus affiliate), which is again what the legacy query does — an affiliate
 * point spends exactly like an earned one.
 */
export const ClientPoints = z.object({
    earnedTotal: z.number(),
    earnedParking: z.number(),
    earnedExtras: z.number(),
    earnedAffiliate: z.number(),
    spentTotal: z.number(),
    spentParking: z.number(),
    spentExtras: z.number(),
    remainingTotal: z.number(),
    remainingParking: z.number(),
    remainingExtras: z.number(),
});
export const ClientRow = z.object({
    id: z.number().int(),
    email: z.string(),
    /** Membership state. Legacy Czech labels: člen / zablokován / smazán. */
    status: ClientStatus,
    tier: ClubTier,
    /**
     * 'YYYY-MM-DD HH:MM:SS' or null. Legacy stores 0000-00-00 for 72 staging
     * rows and NULL for 249 more; both arrive here as null rather than as the
     * year zero, because 1. 1. 0000 on screen is a bug report waiting to be
     * filed against something that is not broken.
     */
    registration: z.string().nullable(),
    /**
     * When the member first signed in to the customer portal, or null if they
     * never have. 41,488 of 67,119 staging members have never logged in — the
     * account was opened for them at the desk. The old screen labels those
     * "neaktivní" and so does this one.
     */
    firstLogin: z.string().nullable(),
    /** Free text staff write about the customer. Never leaves the extranet. */
    internalNote: z.string(),
    /** The member who recruited this one, for affiliate points. */
    parentId: z.number().int().nullable(),
    /** How many members this one has recruited. */
    affiliates: z.number().int(),
    /**
     * Reservations that count: everything except noshow, canceled and deleted.
     * The legacy list hardcodes 0 in this column for every member — see the
     * header comment in api/src/read/clients.ts.
     */
    reservations: z.number().int(),
    /**
     * When the member was last actually here: the latest `departure_real`
     * among counting reservations, or null if none of their stays has ended.
     * Not the booked departure — that is a plan, and a plan can be in the
     * future or simply mistyped. See clientReservationStatsSql.
     */
    lastDeparture: z.string().nullable(),
    /** Latest booking creation among counting reservations, or null. */
    lastCreated: z.string().nullable(),
    /**
     * The profile this member is identified by on screen: their preferred one
     * when it resolves, otherwise their newest non-deleted one. Null when they
     * have no profile at all, which is most of the member table.
     */
    identity: ClientProfile.nullable(),
    points: ClientPoints,
});
export const ClientQuery = z.object({
    /** Free text over id, e-mail, internal note and the preferred profile. */
    q: z.string().trim().max(200).optional(),
    /**
     * Membership state, plus the two derived values the legacy screen offers:
     * 'pending' (a member who has never logged in) and 'active' (one who has).
     * The legacy 'notmember' option is gone: it emitted `cu.id IS NULL` against
     * `FROM club_user cu`, so it could never match a row.
     */
    status: z.enum(CLIENT_STATUS_FILTERS).optional(),
    tier: ClubTier.optional(),
    page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
    /**
     * Each listed page costs five batched side queries regardless of size, so
     * the cap is about rendering 67k members' worth of columns in a browser,
     * not about round trips.
     */
    perPage: z.coerce.number().int().min(10).max(100).default(25),
    sort: ClientSort.default('email'),
    dir: z.enum(['asc', 'desc']).default('asc'),
});
export const ClientPage = z.object({
    rows: z.array(ClientRow),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
    /**
     * Summed over the members ON THIS PAGE, following the same rule as
     * BalancePage.totals: the footer says so in words, so the figure and its
     * caption cannot come apart. A whole-filter total would mean a second pass
     * over 102k point rows for a number nobody reconciles against anything.
     */
    totals: ClientPoints,
});
/** One line of the points ledger — the legacy club_points page's row. */
export const ClubPointRow = z.object({
    id: z.number().int(),
    /** 'YYYY-MM-DD HH:MM:SS' Prague. */
    added: z.string(),
    reservationId: z.number().int().nullable(),
    pointsParking: z.number(),
    pointsExtras: z.number(),
    note: z.string(),
    /** The recruited member these points came from, when they are affiliate points. */
    sourceClubUserId: z.number().int().nullable(),
});
export const CLUB_POINTS_CAP = 100;
/** A manual ledger line: the desk's bonus or correction, as the legacy edit form took it. */
export const ClubPointCreate = z
    .object({
    pointsParking: z.number().int().min(-100_000).max(100_000).default(0),
    pointsExtras: z.number().int().min(-100_000).max(100_000).default(0),
    note: z.string().trim().max(255).default(''),
    reservationId: z.number().int().positive().nullable().optional(),
})
    .refine((v) => v.pointsParking !== 0 || v.pointsExtras !== 0, { message: 'Zadejte body — za parkování, za služby, nebo obojí.' });
export const ClientDetail = z.object({
    client: ClientRow,
    /** The points ledger, newest first, capped at CLUB_POINTS_CAP. */
    points: z.array(ClubPointRow),
    pointsCapped: z.boolean(),
    /** Every profile, newest first, deleted ones included and flagged. */
    profiles: z.array(ClientProfile),
    /** Booking history, newest arrival first. */
    reservations: z.array(ReservationRow),
    /**
     * True when the history hit the cap and older bookings are not shown. The
     * legacy detail page asks for 999,999 rows; the busiest staging member has
     * 86, so the cap is generous — but an unbounded query keyed on an id from
     * the URL bar is how a slow page becomes an outage.
     */
    reservationsCapped: z.boolean(),
});
/* ------------------------------------------------------------------ *
 * Statistiky (read model)
 * ------------------------------------------------------------------ */
/**
 * The legacy Rezervace screen has two tabs — the data grid and a stats tab
 * that shares the grid's filterbox. The NextGen navigation promotes that tab
 * to a top-level item, so this contract deliberately reuses ReservationQuery
 * rather than declaring its own filters: the toolbar an operator learned on
 * Rezervace is the same toolbar, with the same words in the same order, and
 * the same URL parameters. Nothing here is a new thing to learn.
 *
 * The paging and sorting fields are dropped because a total has no page.
 */
export const StatsQuery = ReservationQuery.omit({
    sort: true,
    dir: true,
    page: true,
    perPage: true,
}).extend({
    /**
     * Time-series granularity. 'auto' picks days for a range that fits on a
     * chart and months for anything longer; see resolveStatsUnit() and
     * STATS_DAY_MAX_SPAN in ./rules.ts, which are re-exported from here.
     */
    unit: z.enum(['auto', 'day', 'month']).default('auto'),
});
export const STATS_DIMENSIONS = [
    'type',
    'status',
    'location',
    'club',
    'source',
    'partner',
    'hour',
];
export const StatsDimension = z.enum(STATS_DIMENSIONS);
export const StatsBucket = z.object({
    /** The raw grouping value: an enum member, a date prefix, an hour, an id. */
    key: z.string(),
    /**
     * Set only where the human form is data rather than vocabulary — a partner's
     * name. Everywhere else it is null and the screen supplies the Czech word
     * from the same map Rezervace uses, so the two screens cannot drift apart.
     */
    label: z.string().nullable(),
    count: z.number().int(),
    price: z.number(),
    paid: z.number(),
});
export const StatsGroup = z.object({
    dim: StatsDimension,
    buckets: z.array(StatsBucket),
    /**
     * True when the dimension has more distinct values than the chart shows and
     * the tail was folded into a single 'ostatní' bucket. Only `partner` can set
     * it; every other dimension is a small closed enum.
     */
    truncated: z.boolean(),
    /**
     * Which order the buckets arrived in — 'count' for the six ranked
     * dimensions, 'key' for the hour histogram, whose bars are the clock and
     * must not be rearranged.
     *
     * It is on the wire because the screen has a second measure the server does
     * not know about: the "Zobrazit tržby" checkbox swaps every value column
     * from bookings to crowns without refetching, and a list ranked by bookings
     * then draws its bars out of order. Measured on the staging archive, the
     * whole-database partner breakdown puts 520 bookings worth 139 715 Kč above
     * 181 bookings worth 239 910 Kč — a fourth bar visibly shorter than the
     * fifth. The screen re-ranks by what it is showing, and this field is how it
     * knows which dimensions may be re-ranked at all.
     */
    order: z.enum(['count', 'key']),
});
export const StatsSummary = z.object({
    count: z.number().int(),
    price: z.number(),
    paid: z.number(),
    outstanding: z.number(),
    /** price / count, or 0 on an empty result. Rounded to whole crowns. */
    avgPrice: z.number(),
});
export const Stats = z.object({
    summary: StatsSummary,
    /** Which granularity was actually served, after 'auto' was resolved. */
    unit: z.enum(['day', 'month']),
    /** Echoed back so the chart caption can name the column it is counting. */
    dateField: z.enum(['arrival', 'departure', 'created']),
    series: z.array(StatsBucket),
    groups: z.array(StatsGroup),
});
/* ------------------------------------------------------------------ *
 * Sestavy — the three legacy reports Statistiky did not have:
 * Partneři, Promokódy, Prodané služby a upgrady.
 * ------------------------------------------------------------------ */
export const REPORT_KINDS = ['partners', 'promocodes'];
export const ReportKind = z.enum(REPORT_KINDS);
/** The Rezervace filter set, as Statistiky takes it, minus the chart unit. */
export const ReportQuery = StatsQuery.omit({ unit: true });
/** One partner, or one promo code. */
export const ReportRow = z.object({
    /** The partner's user id, or the promo code itself. */
    key: z.string(),
    label: z.string(),
    /** Promokódy: the pricelist(s) the code was booked on. */
    detail: z.string().nullable(),
    count: z.number().int(),
    /** The legacy `price_total` summed: parking + services + late penalty. */
    revenue: z.number(),
    avgPrice: z.number(),
    /** Average billable days; null when no row had usable dates. */
    avgDays: z.number().nullable(),
});
export const Report = z.object({
    kind: ReportKind,
    rows: z.array(ReportRow),
    /** The legacy footer: rows, bookings, revenue, and the two averages over all bookings. */
    total: z.object({
        groups: z.number().int(),
        count: z.number().int(),
        revenue: z.number(),
        avgPrice: z.number(),
        avgDays: z.number().nullable(),
    }),
});
/* Sestavy → Reklama: Google Ads spend next to the direct bookings of the month (docs/GOOGLE-ADS.md). */
export const AdsQuery = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const AdsReportRow = z.object({
    /** YYYY-MM, or 'total' for the footer. */
    month: z.string(),
    /** Kč spent on Google Ads, whole crowns. */
    cost: z.number(),
    clicks: z.number().int(),
    /** Google's own conversion count (its conversion action, not our bookings). */
    conversions: z.number().int(),
    /** Direct bookings created in the month (source=smile, not deleted). */
    bookings: z.number().int(),
    /** Their parking price, whole crowns. */
    revenue: z.number(),
    /** Of those, bookings without a club account — the customers an ad could have brought (the ceiling). */
    newBookings: z.number().int(),
    newRevenue: z.number(),
    /** Bookings carrying a Google click id (the website writes it since 28 October 2026) — the exact figure. */
    adBookings: z.number().int(),
    adRevenue: z.number(),
    /** cost / bookings; null without bookings. */
    costPerBooking: z.number().nullable(),
    /** cost / newBookings; null without them. */
    costPerNewBooking: z.number().nullable(),
    /** cost / adBookings; null without them. */
    costPerAdBooking: z.number().nullable(),
    /** cost as % of revenue, one decimal; null without revenue. */
    costShare: z.number().nullable(),
    /** revenue / cost, one decimal; null without spend. */
    revenuePerCrown: z.number().nullable(),
});
export const AdsReport = z.object({
    rows: z.array(AdsReportRow),
    total: AdsReportRow,
    /** When the figures were last pulled from Google (ISO), null = never. */
    fetchedAt: z.string().nullable(),
    /** The earliest day in the store, YYYY-MM-DD. */
    firstDate: z.string().nullable(),
    customerId: z.string(),
});
/* Sestavy → Poptávka: the quote log (docs/PRICING-INSTRUMENTATION.md). */
export const DemandRow = z.object({
    /** Arrival day, YYYY-MM-DD. */
    day: z.string(),
    type: z.string(),
    channel: z.string(),
    /** Distinct quotes (one per channel, arrival day, departure day, product and hour). */
    quotes: z.number().int(),
    /** Of those, refused: no price, no place, or the park closed. */
    refused: z.number().int(),
    avgPrice: z.number().nullable(),
    /** Bookings the new system made for that arrival day and product. */
    bookings: z.number().int(),
    bookedPrice: z.number(),
});
export const DemandMonth = z.object({
    month: z.string(),
    quotes: z.number().int(),
    refused: z.number().int(),
    bookings: z.number().int(),
    bookedPrice: z.number(),
});
export const DemandReport = z.object({
    rows: z.array(DemandRow),
    months: z.array(DemandMonth),
    total: DemandMonth,
    /** The earliest arrival day in the chosen range with any row. */
    since: z.string().nullable(),
    /** False when QUOTE_LOG=off on this deployment. */
    enabled: z.boolean(),
});
/**
 * Sestavy → Poptávka, "Hledání v období": the searches MADE in the chosen
 * days (by the moment of the search, Prague time — not by arrival), cut by
 * stay length, how far ahead, hour of the day, weekday, product, the most
 * asked-for arrival dates and the price offered per arrival date. Same
 * de-duplication as the table: one search per channel, arrival day,
 * departure day, product and hour (the owner, 29 October 2026).
 */
export const DemandInsightsQuery = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    channel: z.enum(['all', 'website', 'desk', 'partner']).default('all'),
});
export const DemandBucket = z.object({
    label: z.string(),
    searches: z.number().int(),
    refused: z.number().int(),
    /** Average price offered (searches that got a price). */
    avgPrice: z.number().nullable(),
    /** Average price per day of stay offered. */
    avgPerDay: z.number().nullable(),
    bookings: z.number().int(),
});
export const DemandDate = z.object({
    /** Arrival day, YYYY-MM-DD. */
    date: z.string(),
    searches: z.number().int(),
    refused: z.number().int(),
    avgPrice: z.number().nullable(),
    avgPerDay: z.number().nullable(),
    /** Average price per day by product (cheap / regular / exclusive), null where none offered. */
    perDay: z.record(z.string(), z.number().nullable()),
    /** The usual stay asked for that arrival (median days). */
    medianDays: z.number().nullable(),
    bookings: z.number().int(),
});
export const DemandInsights = z.object({
    enabled: z.boolean(),
    searches: z.number().int(),
    refused: z.number().int(),
    bookings: z.number().int(),
    avgPrice: z.number().nullable(),
    avgPerDay: z.number().nullable(),
    avgDays: z.number().nullable(),
    avgLead: z.number().nullable(),
    /** 24 rows, hour 0–23 of the search, Prague time. */
    byHour: z.array(z.object({ hour: z.number().int(), searches: z.number().int(), bookings: z.number().int() })),
    /** 7 rows, Monday first: the weekday of the search. */
    byWeekday: z.array(z.object({ weekday: z.number().int(), searches: z.number().int(), bookings: z.number().int() })),
    byLength: z.array(DemandBucket),
    byLead: z.array(DemandBucket),
    byProduct: z.array(DemandBucket.extend({ type: z.string() })),
    /** The 20 most asked-for arrival days. */
    topDates: z.array(DemandDate),
    /** Every arrival day asked for, in date order (for the price chart), at most 180. */
    byArrival: z.array(DemandDate),
    /* The website's visit details (recorded from 29 Oct 2026, after the site's upload). */
    /** Distinct website visits (browser sessions) among the searches; null before the site sends them. */
    visits: z.number().int().nullable().default(null),
    /** Of those, visits that ended in a booking. */
    visitsBooked: z.number().int().nullable().default(null),
    /** Searches by bots (search engines, scripts) — left out of every figure above. */
    bots: z.number().int().default(0),
    byDevice: z.array(DemandBucket).default([]),
    byLanguage: z.array(DemandBucket).default([]),
    /** Google Ads click / other / not known. */
    bySource: z.array(DemandBucket).default([]),
    /** The promo codes visitors typed (not the site's own winter "zima"). */
    promos: z.array(z.object({ code: z.string(), searches: z.number().int(), bookings: z.number().int() })).default([]),
    /** Why the refused searches got no price (the website sends it from 4 Nov 2026); '?' = not known (before, or other channels). */
    byRefusal: z.array(z.object({ reason: z.string(), label: z.string(), searches: z.number().int() })).default([]),
});
/* Sestavy → Hodiny: paid hours by role and person (docs/PRICING-INSTRUMENTATION.md). */
export const HoursQuery = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The loaded hourly rate the screen turns hours into money with (the owner's 270 Kč incl. taxes). */
    rate: z.coerce.number().min(0).max(5000).default(270),
});
export const HoursPerson = z.object({
    userId: z.number().int(),
    name: z.string(),
    role: z.enum(['driver', 'dispatch']),
    /** A salaried manager (user status admin): hours shown, cost 0. */
    salaried: z.boolean(),
    hours: z.number().int(),
    /** Driver-days (first→last transfer) or dispatch shifts. */
    days: z.number().int(),
    transfers: z.number().int(),
    transfersPerHour: z.number().nullable(),
    cost: z.number().int(),
});
export const HoursMonth = z.object({
    month: z.string(),
    /** Set when the chosen range covers only part of the month. */
    partialFrom: z.string().nullable(),
    partialTo: z.string().nullable(),
    driverHours: z.number().int(),
    driverDays: z.number().int(),
    drivers: z.number().int(),
    transfers: z.number().int(),
    transfersPerHour: z.number().nullable(),
    dispatchHours: z.number().int(),
    dispatchShifts: z.number().int(),
    dispatchers: z.number().int(),
    /** Hours of salaried managers, inside driverHours/dispatchHours but not in cost. */
    salariedHours: z.number().int(),
    cost: z.number().int(),
    people: z.array(HoursPerson),
});
export const HoursReport = z.object({ months: z.array(HoursMonth), rate: z.number() });
/**
 * Statistiky → Starý systém (the owner, 30 Oct 2026): who still works in the old extranet and on
 * which pages — read from its `usage_log` (page and button names only, no form contents). Admins only.
 */
export const UsageQuery = z.object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const UsageAccount = z.object({
    userId: z.number().int(),
    name: z.string(),
    /** The account's role as the old extranet logged it (admin, manager, driver, partner …). */
    role: z.string(),
    requests: z.number().int(),
    days: z.number().int(),
    /** Prague wall time of the last request. */
    lastAt: z.string(),
    /** The account's pages, most used first. */
    pages: z.array(z.object({ page: z.string(), count: z.number().int() })),
    /** What the account pressed: POSTs by page and the sorted names of the sent fields/buttons (no values), most used first. */
    actions: z.array(z.object({ page: z.string(), keys: z.string(), count: z.number().int() })),
});
export const UsageReport = z.object({
    from: z.string(),
    to: z.string(),
    /** False when the table cannot be read (missing on staging, or no SELECT grant); `error` says why. */
    available: z.boolean(),
    error: z.string().nullable(),
    total: z.number().int(),
    accounts: z.array(UsageAccount),
    pages: z.array(z.object({ page: z.string(), count: z.number().int(), accounts: z.number().int() })),
    /** Every action (POST page + field names) over all accounts. */
    actions: z.array(z.object({ page: z.string(), keys: z.string(), count: z.number().int(), accounts: z.number().int() })),
});
/**
 * Statistiky → Auto místa (the owner, 30 Oct 2026: "I don't see how many were done automatically"):
 * per arrival day, who gave each car its Doporučené místo and whether it was parked there.
 * From the journal: user 0 = the system (autotarget), anyone else = a person. Admins only.
 */
export const AutoTargetDay = z.object({
    date: z.string(),
    /** Bookings arriving that day (not cancelled / no-show / deleted). */
    arrivals: z.number().int(),
    /** The place is the system's and nobody changed it. */
    system: z.number().int(),
    /** The system chose, then a person changed or cleared it. */
    changed: z.number().int(),
    /** A person set it and the system never did. */
    person: z.number().int(),
    /** No place at all. */
    none: z.number().int(),
    /** Of the system's places: cars already parked (Zaparkováno at Europort), and of those parked in the same zone. */
    systemParked: z.number().int(),
    systemSameZone: z.number().int(),
});
export const AutoTargetReport = z.object({
    from: z.string(),
    to: z.string(),
    days: z.array(AutoTargetDay),
    total: AutoTargetDay,
});
export const SOLD_PER_PAGE = 50;
export const SoldQuery = z.object({
    q: z.string().trim().max(120).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    page: z.coerce.number().int().min(1).max(MAX_PAGE).default(1),
});
/** One sale: a service added or removed, or a tariff changed. */
export const SoldRow = z.object({
    /** The journal key, echoed back for the "počítat" toggle. `changed` as stored (UTC). */
    changedStored: z.string(),
    reservationId: z.number().int(),
    item: z.enum(['extraservice', 'type']),
    externalId: z.number().int(),
    /** Prague wall-clock, 'YYYY-MM-DD HH:MM:SS'. */
    changed: z.string(),
    oldValue: z.string(),
    newValue: z.string(),
    amount: z.number(),
    countCommission: z.boolean(),
    userId: z.number().int().nullable(),
    userName: z.string(),
    driverName: z.string(),
    plate: z.string(),
});
/** One dispatcher's line on the "Provize dispečerů" tab. */
export const SoldStanding = z.object({
    userId: z.number().int(),
    userName: z.string(),
    /** 'type' for upgrades, else the service id as a string. */
    columns: z.record(z.string(), z.number()),
    total: z.number(),
    /** Σ round(column / 10), as the legacy computed it. */
    commission: z.number(),
});
export const SoldReport = z.object({
    rows: z.array(SoldRow),
    total: z.number().int(),
    page: z.number().int(),
    perPage: z.number().int(),
    standing: z.array(SoldStanding),
    /** The service columns the standing table needs, catalogue order. */
    services: z.array(z.object({ id: z.number().int(), title: z.string() })),
});
export const CommissionUpdate = z.object({
    changedStored: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
    reservationId: z.number().int().positive(),
    item: z.enum(['extraservice', 'type']),
    externalId: z.number().int().min(0),
    countCommission: z.boolean(),
});
/* Pricing v3 for the api's shadow quotes (4 Nov 2026): the engine and the committed proposal, nothing wired to a price. */
import { priceV3 as _priceV3, pragueDay as _pragueDay } from './pricing-v3.js';
import { PROPOSAL_2026_10_31 as _V3_PROPOSAL } from './pricing-v3-proposal.js';
export const pricingV3 = { priceV3: _priceV3, pragueDay: _pragueDay, PROPOSAL: _V3_PROPOSAL };
